/**
 * Bamoa WebRTC-Hook (Welle 5) - Stele-Variante.
 *
 * Architektur:
 *  - Pro Kamera-Rolle (webcam/doccam) eine CameraPipeline:
 *    Master-Stream (max-Auflösung) ODER Direct-Stream (Ziel-Auflösung)
 *    → Hidden Video → Canvas mit ROI-Crop → captureStream → WebRTC-Track
 *  - Audio in eigenem dauerhaften Stream (wie 4f)
 *  - RTCDataChannel 'bamoa-cam-ctrl' für Zoom/ROI/Scan-Befehle vom Berater
 *  - Beim Swap: nur Canvas-Output-Auflösungen tauschen, Source bleibt stabil
 *  - ROI gehört zur Kamera (deviceId), nicht zur Rolle. Beim Swap behalten Kameras ihren ROI.
 */

import { useEffect, useRef, useState } from 'react';
import { loadIceServers } from '../lib/iceServers';
import { io, Socket } from 'socket.io-client';
import { useSteleStore } from '@/state/store';
import { readConnectionInfo, ConnectionInfo } from './connectionStats';
import { createE2eeSession, applySframe, e2eeForced, sframeSupported, requestEncoderKeyframe, getCryptoStats, E2eeSession } from './sframe';
import {
  CameraPipeline,
  CameraCapabilities,
  ROI_FULL,
  RoiBox,
  clampRoi,
} from './cameraManager';
import {
  CamCommand,
  CamEvent,
  CAM_DATACHANNEL_LABEL,
  SCAN_CHUNK_SIZE,
} from './camControl';
import {
  CameraRole,
  DEFAULT_VIDEO_SETTINGS,
  RESOLUTIONS,
  VideoSettings,
  findAudioDevice,
  isMasterTrackForRole,
  mapDevices,
} from './videoSettings';

export interface TrackMeta {
  streamId: string;
  role: 'main' | 'preview' | 'audio';
  cameraRole?: CameraRole;
  width?: number;
  height?: number;
  frameRate?: number;
}

export interface RtcState {
  status: 'idle' | 'connecting' | 'waiting-offer' | 'negotiating' | 'connected' | 'error' | 'ended';
  error?: string;
  localStreamMain: MediaStream | null;
  localStreamPreview: MediaStream | null;
  remoteStreamMain: MediaStream | null;
  remoteStreamPreview: MediaStream | null;
  remoteStreamAudio: MediaStream | null;
  /** Welle 8c Phase B: Bildschirmfreigabe der Companion-Session (Vorrang als Hauptbild). */
  companionScreenStream: MediaStream | null;
  /** Welle 8d / Mesh M2: Video des zugeschalteten Experten (Magic-Link-Gast). */
  remoteExpertStream: MediaStream | null;
  /** Welle 8d / Mesh M2: Anzeigename des zugeschalteten Experten. */
  expertName: string | null;
  remoteMainMeta: TrackMeta | null;
  remotePreviewMeta: TrackMeta | null;
  hasPreview: boolean;
  /** Welle 7a: Verbindungs- & Verschlüsselungsinfo aus getStats. */
  connectionInfo: ConnectionInfo | null;
  /** Welle 7e: Aufzeichnungs-Status der Gegenseite (Berater) für den Gast-Indikator. */
  remoteRecording: { active: boolean; topic?: string };
  hangup: (reason?: string) => void;
  toggleMic: () => boolean;
  toggleCamera: () => boolean;
  requestSwap: () => void;
  /** Welle 6: NFC-Event vom lokalen Windows-Client an Berater weiterleiten. */
  sendNfcEvent: (payload: Record<string, unknown>) => boolean;
}

interface UseWebRtcOpts {
  rtcToken: string | null;
  role: 'agent' | 'stele';
  steleId?: string | null;
  onPeerGone?: (reason: string) => void;
}

interface LocalVideoTrackInfo {
  streamId: string;
  role: 'main' | 'preview';
  cameraRole: CameraRole;
  pipeline: CameraPipeline;
  /** Welle 8d/M2: zugehöriger RTP-Sender (für Vorschau an/aus & Swap-bei-Vorschau-aus). */
  sender?: RTCRtpSender;
}

/**
 * Welle 8c-pre5: Video-Encoder zu einem sofortigen Keyframe zwingen (Encoder-Neustart
 * via Encoding-Active-Toggle). generateKeyFrame() ist mit Legacy-createEncodedStreams
 * ein No-Op, daher dieser Weg. Wird auf Anforderung des Beraters aufgerufen, damit das
 * dort empfangene Stele-Bild nach einem Kamerawechsel sofort wieder sauber ist.
 */
/** Welle 8c-pre41: Jitter-Buffer-Ziel der Video-Empfänger. Aktualität vor Glätte
 *  (Kiosk): Default 0 → immer aktuellstes Frame, verspätete Frames werden verworfen
 *  statt nachgespielt. Mit ?jbt=<ms> auf altes Verhalten (z. B. 150) zurückschaltbar.
 *  Gilt nur für Video; Audio behält seinen Default-Buffer. */
const RX_JITTER_TARGET_MS = (() => {
  try {
    const p = new URLSearchParams(window.location.search).get('jbt');
    if (p !== null) { const n = parseInt(p, 10); if (!Number.isNaN(n)) return Math.max(0, n); }
  } catch { /* ignore */ }
  return 0;
})();
function tuneReceiverForLoss(receiver: RTCRtpReceiver, kind: string) {
  if (kind !== 'video') return;
  try {
    const anyR = receiver as any;
    if ('jitterBufferTarget' in anyR) { anyR.jitterBufferTarget = RX_JITTER_TARGET_MS; return; }
    if ('playoutDelayHint' in anyR) { anyR.playoutDelayHint = RX_JITTER_TARGET_MS / 1000; }
  } catch { /* ignore */ }
}

async function forceVideoKeyframe(pc: RTCPeerConnection): Promise<void> {
  const senders = pc.getSenders().filter((s) => s.track?.kind === 'video' && s.track.readyState === 'live');
  // Welle 8c-pre23: Worker-Transform auf den Sendern → sauberer Keyframe via generateKeyFrame().
  const w = window as any;
  const usingWorker = typeof w.RTCRtpScriptTransform !== 'undefined'
    && senders.some((s) => (s as any).transform instanceof w.RTCRtpScriptTransform);
  if (usingWorker) { requestEncoderKeyframe(); return; }
  for (const sender of senders) {
    try {
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
      const wasActive = params.encodings[0].active !== false;
      params.encodings[0].active = false;
      await sender.setParameters(params);
      await new Promise((r) => setTimeout(r, 50));
      const p2 = sender.getParameters();
      if (p2.encodings && p2.encodings[0]) p2.encodings[0].active = wasActive;
      await sender.setParameters(p2);
    } catch (e) {
      console.warn('[rtc] forceVideoKeyframe fehlgeschlagen', e);
    }
  }
}

// Welle 8c-pre25: Diagnose (siehe Agent — identische [diag]-Zeile).
interface DiagPrev {
  t: number; rxDec: number; rxDrop: number; rxRecv: number; rxKf: number;
  rxLost: number; rxPli: number; rxNack: number; rxBytes: number; rxFreeze: number;
  txEnc: number; txKf: number; txBytes: number;
  cEnc: number; cOk: number; cFail: number; cPass: number;
}

/** Welle 8c-pre31/32: kompakte Kennzahlen, die die Stele dem Berater über cam-ctrl schickt.
 *  Ab pre32 zusätzlich kumulierte Aggregate für die End-of-Call-Zusammenfassung. */
export type QualityLevel = 'good' | 'fair' | 'bad';
export interface PeerStats {
  side: 'agent' | 'stele';
  level: QualityLevel;
  rttMs: number | null; jitterMs: number | null;
  lossPct: number;
  rxKbps: number; rxFps: number;
  width: number; height: number; codec: string;
  freezeCount: number;
  relay: boolean;
  peakRttMs: number | null; avgRttMs: number | null; maxJitterMs: number | null;
  freezeSeconds: number; packetsLost: number;
  callSeconds: number;
}
interface StatsAccum {
  callStart: number; peakRtt: number; maxJitter: number;
}
function gradeQuality(opts: {
  freezeDelta: number; cryptoFailDelta: number; lossPct: number; rttMs: number | null; relay: boolean;
}): QualityLevel {
  const { freezeDelta, cryptoFailDelta, lossPct, rttMs, relay } = opts;
  if (freezeDelta > 0 || cryptoFailDelta > 0 || lossPct >= 3 || (rttMs != null && rttMs > 500)) return 'bad';
  if (relay || lossPct >= 1 || (rttMs != null && rttMs > 250)) return 'fair';
  return 'good';
}

function logRtcDiag(
  stats: RTCStatsReport,
  crypto: { enc: number; decOk: number; decFail: number; decPass: number },
  prevRef: { current: DiagPrev | null },
  label: string,
  accumRef: { current: StatsAccum | null },
): PeerStats | null {
  const byId = new Map<string, any>();
  let rx: any = null, tx: any = null, pair: any = null;
  stats.forEach((r: any) => {
    byId.set(r.id, r);
    if (r.type === 'inbound-rtp' && r.kind === 'video') rx = r;
    else if (r.type === 'outbound-rtp' && r.kind === 'video') tx = r;
    else if (r.type === 'candidate-pair' && (r.nominated || r.selected)) pair = r;
  });
  let codec = '?';
  if (rx?.codecId && byId.get(rx.codecId)) codec = String(byId.get(rx.codecId).mimeType || '').replace('video/', '');
  let path = '?', relay = '';
  let relayed = false;
  if (pair) {
    const lc = byId.get(pair.localCandidateId), rc = byId.get(pair.remoteCandidateId);
    path = `${lc?.candidateType ?? '?'}/${rc?.candidateType ?? '?'}`;
    if (lc?.relayProtocol) relay = ` relay:${lc.relayProtocol}`;
    relayed = lc?.candidateType === 'relay' || rc?.candidateType === 'relay' || !!lc?.relayProtocol;
  }
  const now = Date.now();
  const cur: DiagPrev = {
    t: now,
    rxDec: rx?.framesDecoded ?? 0, rxDrop: rx?.framesDropped ?? 0, rxRecv: rx?.framesReceived ?? 0,
    rxKf: rx?.keyFramesDecoded ?? 0, rxLost: rx?.packetsLost ?? 0, rxPli: rx?.pliCount ?? 0, rxNack: rx?.nackCount ?? 0,
    rxBytes: rx?.bytesReceived ?? 0, rxFreeze: rx?.freezeCount ?? 0,
    txEnc: tx?.framesEncoded ?? 0, txKf: tx?.keyFramesEncoded ?? 0, txBytes: tx?.bytesSent ?? 0,
    cEnc: crypto.enc, cOk: crypto.decOk, cFail: crypto.decFail, cPass: crypto.decPass,
  };
  const p = prevRef.current;
  prevRef.current = cur;
  if (!p) return null;
  const dt = (now - p.t) / 1000;
  if (dt <= 0) return null;
  const ps = (c: number, q: number) => Math.round((c - q) / dt);
  const brKbps = Math.round(((cur.txBytes - p.txBytes) * 8 / dt) / 1000);
  const rttMs = pair?.currentRoundTripTime != null ? Math.round(pair.currentRoundTripTime * 1000) : null;
  console.log(
    `[diag:${label}] rx: dec=${ps(cur.rxDec, p.rxDec)}/s drop=${ps(cur.rxDrop, p.rxDrop)}/s recv=${ps(cur.rxRecv, p.rxRecv)}/s `
    + `kf=${cur.rxKf - p.rxKf} lost=${cur.rxLost - p.rxLost} pli=${cur.rxPli - p.rxPli} nack=${cur.rxNack - p.rxNack} `
    + `${rx?.frameWidth ?? '?'}x${rx?.frameHeight ?? '?'} ${codec}`
    + ` | crypto: ok=${ps(cur.cOk, p.cOk)}/s fail=${ps(cur.cFail, p.cFail)}/s pass=${ps(cur.cPass, p.cPass)}/s enc=${ps(cur.cEnc, p.cEnc)}/s`
    + ` | tx: enc=${ps(cur.txEnc, p.txEnc)}/s kf=${cur.txKf - p.txKf} ${brKbps}kbps`
    + ` | pair: ${path}${relay} rtt=${rttMs ?? '?'}ms`,
  );
  const pktLost = rx?.packetsLost ?? 0;
  const pktRecv = rx?.packetsReceived ?? 0;
  const lossDenom = pktLost + pktRecv;
  const lossPct = lossDenom > 0 ? Math.round((pktLost / lossDenom) * 1000) / 10 : 0;
  const jitterMs = rx?.jitter != null ? Math.round(rx.jitter * 1000) : null;
  const freezeDelta = cur.rxFreeze - p.rxFreeze;
  const cryptoFailDelta = cur.cFail - p.cFail;
  const rxKbps = Math.round(((cur.rxBytes - p.rxBytes) * 8 / dt) / 1000);
  // Welle 8c-pre32: kumulierte Aggregate für die End-of-Call-Zusammenfassung
  if (!accumRef.current) accumRef.current = { callStart: now, peakRtt: 0, maxJitter: 0 };
  const acc = accumRef.current;
  if (rttMs != null) acc.peakRtt = Math.max(acc.peakRtt, rttMs);
  if (jitterMs != null) acc.maxJitter = Math.max(acc.maxJitter, jitterMs);
  const avgRtt = (pair?.responsesReceived > 0 && pair?.totalRoundTripTime != null)
    ? Math.round((pair.totalRoundTripTime / pair.responsesReceived) * 1000) : null;
  return {
    side: 'stele',
    level: gradeQuality({ freezeDelta, cryptoFailDelta, lossPct, rttMs, relay: relayed }),
    rttMs, jitterMs, lossPct,
    rxKbps, rxFps: ps(cur.rxDec, p.rxDec),
    width: rx?.frameWidth ?? 0, height: rx?.frameHeight ?? 0, codec,
    freezeCount: rx?.freezeCount ?? 0, relay: relayed,
    peakRttMs: acc.peakRtt || null, avgRttMs: avgRtt, maxJitterMs: acc.maxJitter || null,
    freezeSeconds: Math.round((rx?.totalFreezesDuration ?? 0) * 10) / 10,
    packetsLost: pktLost,
    callSeconds: Math.round((now - acc.callStart) / 1000),
  };
}

/** Welle 8c-pre28/32: Dependency-Descriptor-RTP-Erweiterung aus dem SDP entfernen.
 *  Mit aktivem Encoded-Transform (E2EE) zwingt eine DD-Interop-Schwäche zwischen
 *  unterschiedlichen Chromium-Versionen den Encoder in den Nur-Keyframe-Modus
 *  (massive Block-Artefakte). Ohne den DD-extmap entfällt die Schwäche. Zusammen mit
 *  VP9 (siehe preferVp9) ist dies der am Gerät bestätigte, festgezurrte Default
 *  (CefSharp 141 ↔ Chrome 148, E2EE an: beide Richtungen kf=0, crypto fail=0).
 *  Defensiv: bei Fehler unverändert zurück. ?dd=keep behält ihn (Vergleichstest). */
function stripDependencyDescriptor(sdp: string): string {
  try {
    return sdp
      .split('\r\n')
      .filter((line) => !(line.startsWith('a=extmap:') && line.includes('dependency-descriptor')))
      .join('\r\n');
  } catch { return sdp; }
}

/** Welle 8c-pre32: prüft eine ausgehandelte LocalDescription gegen den festgezurrten
 *  Default und WARNT bei Abweichung — so wird eine stille Regression (DD doch im SDP)
 *  im Langzeittest-Log sofort sichtbar, statt unbemerkt den Keyframe-Bug zurückzubringen. */
function assertNoDependencyDescriptor(sdp: string | null | undefined, ddStrip: boolean, label: string): void {
  if (!ddStrip || !sdp) return;
  if (sdp.includes('dependency-descriptor')) {
    console.warn(`[rtc] ⚠ REGRESSION: Dependency-Descriptor trotz Strip noch im ${label}-SDP — Keyframe-Bug-Risiko!`);
  }
}

/** Welle 8c-pre29/32: VP9 als bevorzugten Video-Codec aushandeln (Stele ist Offerer →
 *  gilt für beide Richtungen). VP9 ist bei gleicher Bitrate deutlich effizienter und
 *  hat ein robusteres RTP-Handling als VP8. Zusammen mit dem DD-Strip ist VP9 der am
 *  Gerät bestätigte, festgezurrte Default. ?vp9=0 belässt es bei der Default-Aushandlung
 *  (Vergleichstest, i. d. R. VP8). */
function preferVp9(pc: RTCPeerConnection): void {
  try {
    if (new URL(window.location.href).searchParams.get('vp9') === '0') return;
  } catch { /* ignore */ }
  try {
    const caps = (RTCRtpReceiver as any).getCapabilities?.('video');
    if (!caps?.codecs) return;
    const isVp9 = (c: any) => /vp9/i.test(c.mimeType);
    const vp9 = caps.codecs.filter(isVp9);
    if (!vp9.length) return;
    const ordered = [...vp9, ...caps.codecs.filter((c: any) => !isVp9(c))];
    let applied = 0;
    for (const t of pc.getTransceivers()) {
      if (t.sender?.track?.kind === 'video' && typeof (t as any).setCodecPreferences === 'function') {
        try { (t as any).setCodecPreferences(ordered); applied++; } catch (e) { console.warn('[rtc] setCodecPreferences VP9 fehlgeschlagen', e); }
      }
    }
    if (applied) console.log(`[rtc] Codec-Präferenz: VP9 bevorzugt (${applied} Video-Transceiver)`);
  } catch (e) { console.warn('[rtc] preferVp9 fehlgeschlagen', e); }
}

export function useWebRtc({ rtcToken, role, steleId, onPeerGone }: UseWebRtcOpts): RtcState {
  const [status, setStatus] = useState<RtcState['status']>('idle');
  const [error, setError] = useState<string | undefined>();
  const [localStreamMain, setLocalStreamMain] = useState<MediaStream | null>(null);
  const [localStreamPreview, setLocalStreamPreview] = useState<MediaStream | null>(null);
  const [remoteStreamMain, setRemoteStreamMain] = useState<MediaStream | null>(null);
  const [remoteStreamPreview, setRemoteStreamPreview] = useState<MediaStream | null>(null);
  const [remoteStreamAudio, setRemoteStreamAudio] = useState<MediaStream | null>(null);
  // Welle 8c Phase B: Screen-Share des Companion (zweite Berater-Session). Wenn gesetzt,
  // zeigt die Stele dies als Hauptbild für den Gast (Vorrang vor A/V-Berater-Bild).
  const [companionScreenStream, setCompanionScreenStream] = useState<MediaStream | null>(null);
  const [remoteExpertStream, setRemoteExpertStream] = useState<MediaStream | null>(null);
  const [expertName, setExpertName] = useState<string | null>(null);
  const [remoteMainMeta, setRemoteMainMeta] = useState<TrackMeta | null>(null);
  const [remotePreviewMeta, setRemotePreviewMeta] = useState<TrackMeta | null>(null);
  const [hasPreview, setHasPreview] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);
  // Welle 7e: meldet ob die Gegenseite (Berater) aufzeichnet → Indikator für den Gast
  const [remoteRecording, setRemoteRecording] = useState<{ active: boolean; topic?: string }>({ active: false });

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const statsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Welle 8c-pre16: Freeze-Watchdog gegen dauerhafte Decoder-Artefakte
  const inboundStatsRef = useRef<Map<number, { f: number; p: number; pli: number; kf: number }>>(new Map());
  const lastKeyframeReqRef = useRef<number>(0);
  const diagPrevRef = useRef<DiagPrev | null>(null); // Welle 8c-pre25: Diagnose-Vorwerte
  const statsAccumRef = useRef<StatsAccum | null>(null); // Welle 8c-pre32: kumulierte Aggregate für die Summary
  const codecLockWarnedRef = useRef(false); // Welle 8c-pre32: VP9-Codec-Check nur einmal pro Call warnen
  const iceStartRef = useRef<number | null>(null); // Welle 8c-pre35: Startzeit der ICE-Sammlung (Kandidaten-Timing)
  // Welle 8c-pre21: periodischer Keyframe als Absicherung gegen nicht erkennbare
  // Decoder-Korruption (z.B. korrupt akzeptierter Keyframe nach einem Burst). Heilt
  // jede Korruption innerhalb des Intervalls, auch wenn kein Stats-Signal sie verrät.
  const kfTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Welle 8c-pre17: maximale Robustheit. Das P2P-Medium ist die Wahrheit über die
  // Lebendigkeit des Calls; der Signaling-Socket ist wegwerfbar. Diese Refs steuern
  // ICE-Restart-Recovery und den verzögerten Teardown nur bei echtem Medien-Tod.
  const mediaEverConnectedRef = useRef(false);
  const lastIceRestartRef = useRef<number>(0);
  const mediaDeadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Welle 8c-pre18: Reconnect-Karenz bei transientem bye (Reload/Unload des Beraters)
  const byeGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Welle 7c/7e: SFrame-Session bei Capability; Engagement steuert der Intent (Tenant-Policy).
  const e2eeRef = useRef<E2eeSession | null>(null);
  const e2eeIntentRef = useRef<boolean>(false);
  const socketRef = useRef<Socket | null>(null);
  // Welle 8b: Socket-ID des Peers für targeted signaling
  const peerIdRef = useRef<string | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  // Welle 8c Phase A: separater Companion-Pfad (zweite Berater-Session, kein A/V).
  // Eigene PeerConnection + Steuerkanal, vollständig getrennt vom A/V-pcRef, damit
  // der A/V-Pfad unberührt bleibt. Die Stele ist hier ANTWORTENDE Seite (der
  // Companion offert). DTLS verschlüsselt den Steuerkanal P2P; kein SFrame in Phase A.
  const companionPcRef = useRef<RTCPeerConnection | null>(null);
  const companionPeerIdRef = useRef<string | null>(null);
  const companionDcRef = useRef<RTCDataChannel | null>(null);
  const companionPendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  // Welle 8c Phase B: eigene SFrame-Session NUR für den Companion-Screen-Track
  // (getrennter Schlüssel, Handshake über den Companion-Steuerkanal, nie über Server).
  const companionScreenE2eeRef = useRef<E2eeSession | null>(null);
  // Welle 8d / Mesh M2: Stele↔Experte-Strecke (Magic-Link-Gast). Die Stele ist hier
  // OFFERER (etablierter Teilnehmer offeriert dem Neuankömmling, der Gast antwortet).
  // Eigene PeerConnection, völlig getrennt vom A/V-pcRef. Transport-verschlüsselt
  // (DTLS-SRTP), KEIN SFrame und KEIN encodedInsertableStreams (sonst stocken die Frames).
  const guestPcRef = useRef<RTCPeerConnection | null>(null);
  const guestPeerIdRef = useRef<string | null>(null);
  // Welle 8d/M2: Vorschau aktuell an? (Ref für den Swap-Handler, damit „Umschalten" auch
  // bei abgeschalteter Vorschau funktioniert.)
  const previewEnabledRef = useRef(true);
  const guestPendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  // Welle 8c-pre: erkannter Geräte-Status, der dem Berater P2P gemeldet wird
  const deviceStatusRef = useRef<{ hasCamera: boolean; hasMic: boolean; hasAudioOut: boolean; message?: string } | null>(null);
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const localAudioTrackRef = useRef<MediaStreamTrack | null>(null);
  const localVideoTracksRef = useRef<LocalVideoTrackInfo[]>([]);
  const pipelinesRef = useRef<Map<CameraRole, CameraPipeline>>(new Map());
  // Welle 8c-pre38: Dokumenten-Signierung (PDF über lokale SignController-API).
  const sigInBufferRef = useRef<{
    sigId: string; filename: string; totalBytes: number; totalChunks: number;
    chunks: ArrayBuffer[]; receivedBytes: number; replyDc: RTCDataChannel | null;
  } | null>(null);
  const sigAbortRef = useRef<AbortController | null>(null);
  const sigJobRef = useRef<string | null>(null);
  // Welle 8c-pre41: Zuverlässigkeit. Bereits gestartete Jobs (Dedup bei Upload-Retry) und
  // offene Result-ACK-Warter (Berater bestätigt Empfang des signierten PDFs).
  const sigStartedRef = useRef<Set<string>>(new Set());
  const sigResultAckRef = useRef<Map<string, () => void>>(new Map());
  const remoteStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  const remoteMetaRef = useRef<Map<string, TrackMeta>>(new Map());
  const settingsRef = useRef<VideoSettings>(DEFAULT_VIDEO_SETTINGS);
  const peerGoneFiredRef = useRef(false);
  const onPeerGoneRef = useRef(onPeerGone);
  onPeerGoneRef.current = onPeerGone;

  const teardown = (reason: string) => {
    if (peerGoneFiredRef.current) return;
    peerGoneFiredRef.current = true;
    console.log(`[rtc] teardown reason=${reason}`);
    try { socketRef.current?.emit('rtc:bye'); } catch {}
    try { dcRef.current?.close(); } catch {}
    dcRef.current = null;
    try { socketRef.current?.disconnect(); } catch {}
    socketRef.current = null;
    peerIdRef.current = null;
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    // Welle 8c Phase A: Companion-Pfad mit beenden
    try { companionDcRef.current?.close(); } catch {}
    companionDcRef.current = null;
    try { companionPcRef.current?.close(); } catch {}
    companionPcRef.current = null;
    companionPeerIdRef.current = null;
    companionPendingIceRef.current = [];
    companionScreenE2eeRef.current = null;
    setCompanionScreenStream(null);
    // Welle 8d / Mesh M2: Experten-Strecke mit beenden
    try { guestPcRef.current?.close(); } catch {}
    guestPcRef.current = null;
    guestPeerIdRef.current = null;
    guestPendingIceRef.current = [];
    setRemoteExpertStream(null);
    setExpertName(null);
    if (statsTimerRef.current) { clearInterval(statsTimerRef.current); statsTimerRef.current = null; }
    if (kfTimerRef.current) { clearInterval(kfTimerRef.current); kfTimerRef.current = null; }
    if (mediaDeadTimerRef.current) { clearTimeout(mediaDeadTimerRef.current); mediaDeadTimerRef.current = null; }
    if (byeGraceTimerRef.current) { clearTimeout(byeGraceTimerRef.current); byeGraceTimerRef.current = null; }
    setConnectionInfo(null);
    setRemoteRecording({ active: false });
    try { localAudioTrackRef.current?.stop(); } catch {}
    localAudioTrackRef.current = null;
    pipelinesRef.current.forEach((p) => p.stop());
    pipelinesRef.current.clear();
    localVideoTracksRef.current = [];
    remoteStreamsRef.current.clear();
    remoteMetaRef.current.clear();
    setLocalStreamMain(null);
    setLocalStreamPreview(null);
    setRemoteStreamMain(null);
    setRemoteStreamPreview(null);
    setRemoteStreamAudio(null);
    setRemoteMainMeta(null);
    setRemotePreviewMeta(null);
    setStatus('ended');
    onPeerGoneRef.current?.(reason);
  };

  function refreshRemoteAssignment() {
    let main: MediaStream | null = null;
    let preview: MediaStream | null = null;
    let audio: MediaStream | null = null;
    let mainMeta: TrackMeta | null = null;
    let previewMeta: TrackMeta | null = null;
    // 1. Explizite Meta-Zuweisungen (kommen von Stele→Berater)
    remoteMetaRef.current.forEach((meta, streamId) => {
      const s = remoteStreamsRef.current.get(streamId);
      if (!s) return;
      if (meta.role === 'main') { main = s; mainMeta = meta; }
      else if (meta.role === 'preview') { preview = s; previewMeta = meta; }
      else if (meta.role === 'audio') { audio = s; }
    });
    // 2. Default-Fallback für Streams ohne Meta (Berater→Stele: ein Stream mit Audio+Video)
    remoteStreamsRef.current.forEach((s, streamId) => {
      if (remoteMetaRef.current.has(streamId)) return;
      const hasAudio = s.getAudioTracks().length > 0;
      const hasVideo = s.getVideoTracks().length > 0;
      if (hasVideo && !main) {
        main = s;
        const vt = s.getVideoTracks()[0];
        const vs = vt.getSettings();
        mainMeta = {
          streamId, role: 'main',
          width: vs.width, height: vs.height, frameRate: vs.frameRate,
        };
      }
      if (hasAudio && !audio) {
        audio = s;
      }
    });
    setRemoteStreamMain(main);
    setRemoteStreamPreview(preview);
    setRemoteStreamAudio(audio);
    setRemoteMainMeta(mainMeta);
    setRemotePreviewMeta(previewMeta);
    setHasPreview(Boolean(preview));
  }

  function sendCapabilities() {
    if (role !== 'stele' || !dcRef.current || dcRef.current.readyState !== 'open') return;
    const caps: CameraCapabilities[] = [];
    pipelinesRef.current.forEach((p) => caps.push(p.getCapabilities()));
    const evt: CamEvent = { type: 'camera.capabilities', cameras: caps };
    try {
      dcRef.current.send(JSON.stringify(evt));
      console.log('[rtc] sent capabilities', caps.length);
    } catch (e) {
      console.warn('[rtc] send capabilities failed', e);
    }
  }

  /** Welle 8c-pre: Geräte-Status P2P an den Berater melden (DataChannel, kein Server). */
  function sendDeviceStatus() {
    const dc = dcRef.current;
    const st = deviceStatusRef.current;
    if (!dc || dc.readyState !== 'open' || !st) return;
    try {
      dc.send(JSON.stringify({ type: 'device-status', ...st }));
      console.log('[rtc] device-status gesendet (P2P):', st.message ?? 'alle Geräte ok');
    } catch (e) {
      console.warn('[rtc] send device-status failed', e);
    }
  }

  function sendRoiUpdated(camRole: 'main' | 'preview', pipeline: CameraPipeline, replyDc: RTCDataChannel | null = dcRef.current) {
    if (!replyDc || replyDc.readyState !== 'open') return;
    const evt: CamEvent = {
      type: 'camera.roi.updated',
      role: camRole,
      cameraRole: pipeline.cameraRole,
      roi: pipeline.roi,
      effectiveZoom: Number((1 / pipeline.roi.w).toFixed(2)),
      isDetail: pipeline.masterTrack && (1 / pipeline.roi.w) <= pipeline.getMaxDetailZoom(),
    };
    try { replyDc.send(JSON.stringify(evt)); } catch (e) { console.warn('[rtc] send roi-updated failed', e); }
  }

  function findPipelineForCamRole(camRole: 'main' | 'preview'): { info: LocalVideoTrackInfo; pipeline: CameraPipeline } | null {
    const info = localVideoTracksRef.current.find((t) => t.role === camRole);
    if (!info) return null;
    return { info, pipeline: info.pipeline };
  }

  function handleCamCommand(cmd: CamCommand, replyDc: RTCDataChannel | null = dcRef.current) {
    if (role !== 'stele') return;
    // Welle 8c-pre5: Keyframe-Anforderung vom Berater (nach dessen Kamerawechsel).
    if (cmd.type === 'camera.keyframe') {
      const pc = pcRef.current;
      if (pc) void forceVideoKeyframe(pc);
      return;
    }
    // Welle 8d/M2: Vorschau-Stream an/aus. Bei „aus" wird der Preview-Sender per
    // replaceTrack(null) gestoppt → die Bandbreite steht dem Hauptbild zur Verfügung.
    // Keine Neuverhandlung. „Umschalten" bleibt möglich (siehe Swap-Handler).
    if (cmd.type === 'camera.preview.set') {
      previewEnabledRef.current = cmd.on;
      const previewInfo = localVideoTracksRef.current.find((t) => t.role === 'preview');
      const sender = previewInfo?.sender;
      const track = previewInfo?.pipeline?.outputTrack ?? null;
      if (sender) {
        void sender.replaceTrack(cmd.on ? track : null);
        console.log(`[rtc] Vorschau → ${cmd.on ? 'an' : 'aus (Bandbreite fürs Hauptbild)'}`);
      }
      return;
    }
    // Welle 8c-pre49: Dokumentenbeleuchtung. Relais-Kanal 1 (Licht) über die lokale
    // Client-REST-API der Stele schalten — gleiche Basis wie der Signier-Flow. Braucht
    // keine Kamera-Pipeline → vor dem Pipeline-Lookup. Ergebnis (oder Fehler) wird als
    // light.state an den Berater zurückgemeldet, damit dessen Button den realen Zustand zeigt.
    if (cmd.type === 'light.set') {
      const want = cmd.on;
      const base = (window as any).__BAMOA_SIGN_BASE__ || 'http://127.0.0.1:8080';
      void (async () => {
        let ok = false;
        let error: string | undefined;
        try {
          const r = await fetch(`${base}/Relais/setStatus?channel=1&state=${want ? 'on' : 'off'}`);
          const body = (await r.text()).trim().toLowerCase();
          ok = r.ok && body.includes('ok');
          if (!ok) error = `Relais antwortete HTTP ${r.status} ${body}`.trim();
        } catch (e) {
          error = (e as Error).message || 'lokaler Client nicht erreichbar';
        }
        console.log(`[rtc] Licht (Relais K1) → ${want ? 'an' : 'aus'}: ${ok ? 'ok' : 'FEHLER: ' + error}`);
        if (replyDc?.readyState === 'open') {
          try { replyDc.send(JSON.stringify({ type: 'light.state', on: want, ok, error } as CamEvent)); } catch { /* */ }
        }
      })();
      return;
    }
    // Welle 8c-pre38: Dokumenten-Signierung (PDF). Braucht keine Kamera-Pipeline, daher
    // VOR dem Pipeline-Lookup. submit.start eröffnet den PDF-Empfangspuffer, submit.end
    // startet den lokalen Signier-Aufruf, abort löst /Sign/getkill aus.
    if (cmd.type === 'sign.submit.start') {
      sigInBufferRef.current = {
        sigId: cmd.sigId, filename: cmd.filename || 'dokument.pdf',
        totalBytes: cmd.totalBytes, totalChunks: cmd.totalChunks,
        chunks: [], receivedBytes: 0, replyDc,
      };
      sigJobRef.current = cmd.sigId;
      console.log(`[rtc] sign: PDF-Empfang erwartet ${cmd.totalBytes} B in ${cmd.totalChunks} Chunks (${cmd.filename})`);
      return;
    }
    if (cmd.type === 'sign.submit.end') {
      const buf = sigInBufferRef.current;
      if (!buf || buf.sigId !== cmd.sigId) {
        // Upload-Retry, dessen Puffer schon verarbeitet wurde: nur erneut bestätigen.
        if (sigStartedRef.current.has(cmd.sigId) && replyDc?.readyState === 'open') {
          try { replyDc.send(JSON.stringify({ type: 'sign.submit.ack', sigId: cmd.sigId } as CamEvent)); } catch { /* */ }
        } else {
          console.warn('[rtc] sign.submit.end ohne Puffer');
        }
        return;
      }
      if (buf.receivedBytes < buf.totalBytes) {
        console.warn(`[rtc] sign.submit.end unvollständig (${buf.receivedBytes}/${buf.totalBytes}) — warte auf Retry`);
        return; // KEIN ACK → Berater sendet erneut
      }
      const merged = new Uint8Array(buf.totalBytes);
      let off = 0;
      for (const c of buf.chunks) { merged.set(new Uint8Array(c), off); off += c.byteLength; }
      sigInBufferRef.current = null;
      // Empfang vollständig → bestätigen, damit der Berater nicht erneut sendet.
      if (buf.replyDc?.readyState === 'open') {
        try { buf.replyDc.send(JSON.stringify({ type: 'sign.submit.ack', sigId: cmd.sigId } as CamEvent)); } catch { /* */ }
      }
      // Dedup: ein bereits laufender/gelaufener Job darf nicht erneut signoSign2 starten.
      if (sigStartedRef.current.has(cmd.sigId)) return;
      sigStartedRef.current.add(cmd.sigId);
      void runSigningJob(buf.sigId, buf.filename, merged, buf.replyDc);
      return;
    }
    if (cmd.type === 'sign.result.ack') {
      const r = sigResultAckRef.current.get(cmd.sigId);
      if (r) { r(); sigResultAckRef.current.delete(cmd.sigId); }
      return;
    }
    if (cmd.type === 'sign.abort') {
      void abortSigningJob(cmd.sigId, replyDc);
      return;
    }
    const target = findPipelineForCamRole(cmd.role);
    if (!target) {
      console.warn('[rtc] cam-cmd target nicht gefunden für role=', cmd.role);
      // Welle 8c-pre19: Scan-Anforderung an eine fehlende Kamera → Fehler zurückmelden,
      // damit die anfragende Seite (auch der Companion) den Nutzer informieren kann.
      if (cmd.type === 'camera.scan' && replyDc && replyDc.readyState === 'open') {
        try { replyDc.send(JSON.stringify({ type: 'camera.scan.error', role: cmd.role, scanId: 'n/a', error: 'camera-not-found' } as CamEvent)); } catch {}
      }
      return;
    }
    const { pipeline } = target;

    switch (cmd.type) {
      case 'camera.roi.set':
        pipeline.setRoi(cmd.roi);
        sendRoiUpdated(cmd.role, pipeline, replyDc);
        break;
      case 'camera.roi.move': {
        const r = pipeline.roi;
        const next = clampRoi({ x: r.x + cmd.dx, y: r.y + cmd.dy, w: r.w, h: r.h });
        pipeline.setRoi(next);
        sendRoiUpdated(cmd.role, pipeline, replyDc);
        break;
      }
      case 'camera.roi.zoom': {
        const r = pipeline.roi;
        const anchor = cmd.anchor ?? { x: 0.5, y: 0.5 };
        const ax = r.x + r.w * anchor.x;
        const ay = r.y + r.h * anchor.y;
        const newW = Math.max(0.05, Math.min(1, 1 / Math.max(1, cmd.factor)));
        const newH = newW;
        const next = clampRoi({ x: ax - newW * anchor.x, y: ay - newH * anchor.y, w: newW, h: newH });
        pipeline.setRoi(next);
        sendRoiUpdated(cmd.role, pipeline, replyDc);
        break;
      }
      case 'camera.roi.reset':
        pipeline.resetRoi();
        sendRoiUpdated(cmd.role, pipeline, replyDc);
        break;
      case 'camera.scan': {
        const fmt = cmd.format ?? 'jpeg';
        const mime = fmt === 'png' ? 'image/png' : fmt === 'webp' ? 'image/webp' : 'image/jpeg';
        const dc = replyDc;
        if (!dc || dc.readyState !== 'open') return;
        (async () => {
          const scanId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          try {
            const blob = await pipeline.snapshot(mime as any, 0.85);
            if (!blob) {
              dc.send(JSON.stringify({ type: 'camera.scan.error', role: cmd.role, scanId, error: 'no-frame' } as CamEvent));
              return;
            }
            const buf = await blob.arrayBuffer();
            const totalBytes = buf.byteLength;
            const totalChunks = Math.ceil(totalBytes / SCAN_CHUNK_SIZE);
            console.log(`[rtc] scan starten ${fmt} ${(totalBytes / 1024).toFixed(1)} KB in ${totalChunks} chunks`);

            const startEvt: CamEvent = {
              type: 'camera.scan.start',
              role: cmd.role,
              cameraRole: pipeline.cameraRole,
              format: fmt,
              scanId,
              totalBytes,
              totalChunks,
              chunkSize: SCAN_CHUNK_SIZE,
            };
            dc.send(JSON.stringify(startEvt));

            // Welle 8c-pre22: Scan ratenbegrenzt senden, statt den Puffer maximal zu
            // fluten. Ein Burst sättigt sonst den (knappen) Uplink und stört das parallel
            // laufende A/V-Video (Paketverlust → Decoder-Korruption). Wir glätten auf ein
            // Ziel von ~2 Mbit/s und halten den Sendepuffer klein — das verteilt den Scan
            // über ein paar Sekunden und lässt dem Video Luft.
            const TARGET_BYTES_PER_SEC = 250 * 1024; // ~2 Mbit/s
            const BUFFER_THRESHOLD = 48 * 1024;
            const t0 = Date.now();
            let sentBytes = 0;
            for (let i = 0; i < totalChunks; i++) {
              const start = i * SCAN_CHUNK_SIZE;
              const end = Math.min(start + SCAN_CHUNK_SIZE, totalBytes);
              while (dc.bufferedAmount > BUFFER_THRESHOLD) {
                await new Promise((r) => setTimeout(r, 5));
                if (dc.readyState !== 'open') return;
              }
              dc.send(buf.slice(start, end));
              sentBytes += end - start;
              // Sanftes Rate-Limiting: liegen wir vor dem Zeitplan, kurz warten.
              const expectedMs = (sentBytes / TARGET_BYTES_PER_SEC) * 1000;
              const aheadMs = expectedMs - (Date.now() - t0);
              if (aheadMs > 0) await new Promise((r) => setTimeout(r, Math.min(aheadMs, 40)));
            }

            dc.send(JSON.stringify({ type: 'camera.scan.end', scanId } as CamEvent));
            console.log(`[rtc] scan abgeschlossen ${scanId}`);
            // Welle 8c-pre20: Der Scan-Burst (großes Bild über DataChannel) kann den
            // Uplink kurz sättigen und das laufende A/V-Video stören (Fragmente, die
            // sonst stehen bleiben). Direkt danach die A/V-Verbindung auffrischen:
            // lokalen Keyframe erzwingen (→ Berater-Decoder) + Keyframe vom Berater
            // anfordern (→ Stele-Decoder). Unabhängig davon, über welchen Kanal der
            // Scan lief — das A/V-Video läuft immer über pcRef/dcRef.
            const avpc = pcRef.current;
            if (avpc) void forceVideoKeyframe(avpc);
            try { dcRef.current?.send(JSON.stringify({ type: 'camera.keyframe' })); } catch { /* ignore */ }
          } catch (e: any) {
            console.warn('[rtc] scan fehler', e);
            try {
              dc.send(JSON.stringify({ type: 'camera.scan.error', role: cmd.role, scanId, error: e?.message ?? 'snapshot-error' } as CamEvent));
            } catch {}
          }
        })();
        break;
      }
    }
  }

  useEffect(() => {
    if (!rtcToken) return;
    peerGoneFiredRef.current = false;
    statsAccumRef.current = null; // Welle 8c-pre32: Summary-Aggregate für neuen Call zurücksetzen
    diagPrevRef.current = null;
    codecLockWarnedRef.current = false;
    // Welle 8c-pre32: aktive Codec-Konfiguration einmal loggen (Default festgezurrt).
    try {
      const q = new URL(window.location.href).searchParams;
      const vp9 = q.get('vp9') !== '0';
      const ddStrip = q.get('dd') !== 'keep';
      const rawCam = q.get('rawcam') === '1';
      console.log(`[rtc] Codec-Default (festgezurrt): vp9=${vp9 ? 'on' : 'off'} dd=${ddStrip ? 'strip' : 'keep'} rawcam=${rawCam ? 'on' : 'off'} — Overrides: ?vp9=0 ?dd=keep ?rawcam=1`);
    } catch { /* ignore */ }
    let cancelled = false;

    (async () => {
      try {
        setStatus('connecting');

        if (role === 'stele' && steleId) {
          try {
            const r = await fetch(`/api/steles/${steleId}/video-settings`);
            if (r.ok) settingsRef.current = { ...DEFAULT_VIDEO_SETTINGS, ...(await r.json()) };
          } catch {
            console.warn('[rtc] video-settings nicht geladen, nutze Defaults');
          }
        }
        const settings = settingsRef.current;
        console.log('[rtc] settings:', JSON.stringify(settings));

        const iceServers = await loadIceServers();

        // Welle 7c: Insertable Streams nur aktivieren wenn E2EE-Flag gesetzt.
        const pcConfig: RTCConfiguration = { iceServers };
        // Welle 8c-pre35: Kandidaten VORAB sammeln (inkl. srflx über STUN), damit das
        // Offer der Stele bereits einen srflx-Kandidaten enthält und nicht erst host
        // (per mDNS obfuskiert, netzübergreifend unbrauchbar) → Relay → spät srflx.
        // Verkürzt das transiente Relay-first-Fenster zum Gesprächsbeginn.
        pcConfig.iceCandidatePoolSize = 2;
        if (sframeSupported()) (pcConfig as any).encodedInsertableStreams = true;
        const pc = new RTCPeerConnection(pcConfig);
        iceStartRef.current = Date.now(); // Welle 8c-pre35: Referenz für Kandidaten-Timing
        pcRef.current = pc;

        // Welle 7c: E2EE-Session anlegen, BEVOR Tracks gesetzt werden. Nur bei Flag.
        e2eeRef.current = null;
        if (sframeSupported()) {
          try {
            e2eeRef.current = await createE2eeSession();
            console.log('[rtc] E2EE-Session erstellt (opt-in via ?e2ee=1)');
          } catch (err) {
            console.warn('[rtc] E2EE-Setup fehlgeschlagen, fahre ohne SFrame fort', err);
            e2eeRef.current = null;
          }
        }

        // DataChannel-Aufbau (vor dem Offer!). Welle 8c-pre47: als geteilte Funktion,
        // damit der F5-Rebuild (rebuildAvPeer) exakt dieselbe Verdrahtung nutzt.
        function wireCamDataChannel(targetPc: RTCPeerConnection) {
          if (role === 'stele') {
            const dc = targetPc.createDataChannel(CAM_DATACHANNEL_LABEL, { ordered: true });
            dcRef.current = dc;
            dc.binaryType = 'arraybuffer'; // Welle 8c-pre38: PDF-Chunks (Berater → Stele)
            dc.onopen = () => {
              console.log('[rtc] dc open');
              sendCapabilities();
              sendDeviceStatus();
            };
            dc.onclose = () => console.log('[rtc] dc closed');
            dc.onerror = (e) => console.warn('[rtc] dc error', e);
            dc.onmessage = (e) => {
              if (e.data instanceof ArrayBuffer) {
                // Welle 8c-pre38/41: eingehender PDF-Chunk für einen Signier-Job + Fortschritt
                const sb = sigInBufferRef.current;
                if (sb) {
                  sb.chunks.push(e.data);
                  sb.receivedBytes += e.data.byteLength;
                  const pct = sb.totalBytes > 0 ? Math.min(100, Math.round((sb.receivedBytes / sb.totalBytes) * 100)) : 0;
                  window.dispatchEvent(new CustomEvent('bamoa:sign-progress', { detail: { phase: 'receiving', pct } }));
                }
                return;
              }
              try {
                const cmd = JSON.parse(e.data) as CamCommand;
                handleCamCommand(cmd);
              } catch (err) {
                console.warn('[rtc] dc invalid message', err);
              }
            };
          } else {
            targetPc.ondatachannel = (e) => {
              console.log('[rtc] dc received', e.channel.label);
              if (e.channel.label === CAM_DATACHANNEL_LABEL) {
                dcRef.current = e.channel;
                e.channel.onopen = () => console.log('[rtc] dc open (agent)');
                e.channel.onclose = () => console.log('[rtc] dc closed (agent)');
                e.channel.onmessage = (msg) => {
                  try {
                    const evt = JSON.parse(msg.data) as CamEvent;
                    // Wird an Agent-Hook über window-Event geleitet
                    window.dispatchEvent(new CustomEvent('bamoa:cam-event', { detail: evt }));
                  } catch (err) {
                    console.warn('[rtc] dc invalid event from stele', err);
                  }
                };
              }
            };
          }
        }
        wireCamDataChannel(pc);

        // STELE: Audio + Video Setup (Welle 8c-pre: resilient — ein Geräteproblem
        // bricht den Call NICHT ab, sondern wird dem Berater P2P gemeldet. Status
        // basiert auf dem tatsächlichen Öffnen, nicht nur auf dem Vorhandensein.)
        if (role === 'stele') {
          // Permission/Labels anstoßen — Fehler hier ist NICHT fatal
          try {
            const probeStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            probeStream.getTracks().forEach((t) => t.stop());
          } catch (e) {
            console.warn('[rtc] Geräte-Probe fehlgeschlagen (nicht fatal):', (e as Error).name);
          }
          if (cancelled) return;

          const all = await navigator.mediaDevices.enumerateDevices();
          const audioDevs = all.filter((d) => d.kind === 'audioinput').map((d) => ({
            deviceId: d.deviceId, label: d.label || `Mic ${d.deviceId.slice(0, 6)}`, kind: 'audioinput'
          }));
          const videoDevs = all.filter((d) => d.kind === 'videoinput').map((d) => ({
            deviceId: d.deviceId, label: d.label || `Cam ${d.deviceId.slice(0, 6)}`
          }));
          console.log('[rtc] Audio-Devices:', audioDevs.map((d) => d.label));
          console.log('[rtc] Video-Devices:', videoDevs.map((d) => d.label));

          const problems: string[] = [];
          let audioOk = false;
          let mainCamOk = false;
          const hasAudioOut = all.some((d) => d.kind === 'audiooutput');
          const busyName = (n?: string) => n === 'NotReadableError' || n === 'AbortError';

          // AUDIO — Fehler nicht fatal
          const audioDevId = findAudioDevice(audioDevs, settings.audioInputLabel);
          try {
            const audioStream = await navigator.mediaDevices.getUserMedia({
              audio: audioDevId ? { deviceId: { exact: audioDevId } } : true,
              video: false,
            });
            const audioTrack = audioStream.getAudioTracks()[0];
            if (audioTrack) {
              localAudioTrackRef.current = audioTrack;
              pc.addTrack(audioTrack, new MediaStream([audioTrack]));
              audioOk = true;
              console.log('[rtc] Audio-Track aufgebaut, deviceId:', audioDevId);
            } else {
              problems.push('kein Mikrofon');
            }
          } catch (e) {
            const name = (e as Error).name;
            problems.push(busyName(name) ? 'Mikrofon belegt/nicht verfügbar'
              : name === 'NotFoundError' ? 'kein Mikrofon' : 'Mikrofon-Fehler');
            console.warn('[rtc] Audio konnte nicht geöffnet werden:', name);
          }

          // VIDEO: Pipelines — Fehler nicht fatal
          const mapping = mapDevices(videoDevs, settings);
          if (!mapping) {
            problems.push('keine Kamera gefunden');
            console.warn('[rtc] Keine Kamera gemappt — Call läuft ohne Video weiter');
          } else {
            const mainRes = RESOLUTIONS[settings.mainResolution] ?? RESOLUTIONS['720p'];
            const previewRes = RESOLUTIONS[settings.previewResolution] ?? RESOLUTIONS['240p'];

            // Welle 8c-pre45: Webcam im nativen Sensor-Seitenverhältnis aufnehmen
            // (volles Sichtfeld). Diagnose-Rückfall ?webcamfov=crop erzwingt das alte
            // 16:9-Verhalten, falls eine Kamera mit freier Breite Probleme macht.
            let webcamFullFov = true;
            try { webcamFullFov = new URL(window.location.href).searchParams.get('webcamfov') !== 'crop'; } catch { /* ignore */ }
            if (!webcamFullFov) console.warn('[rtc] ?webcamfov=crop — DIAGNOSE: Webcam wird auf 16:9 beschnitten (altes Verhalten)');

            // MAIN: Webcam (oder die als "webcam"-Rolle gemappte Kamera)
            try {
              const webcamLabel = videoDevs.find((d) => d.deviceId === mapping.webcamDeviceId)?.label ?? 'Webcam';
              const mainPipeline = new CameraPipeline({
                cameraRole: 'webcam',
                deviceId: mapping.webcamDeviceId,
                label: webcamLabel,
                masterTrack: isMasterTrackForRole('webcam', settings),
                outputWidth: mainRes.width,
                outputHeight: mainRes.height,
                outputFps: settings.mainFps,
                masterFps: settings.mainFps,
                preserveSourceAspect: webcamFullFov,
              });
              await mainPipeline.start();
              pipelinesRef.current.set('webcam', mainPipeline);
              const mainStream = mainPipeline.outputStream!;
              // Welle 8c-pre27: Diagnose-Schalter ?rawcam=1 → rohen Kamera-Track senden
              // (ohne Canvas/ROI). Bestätigt, ob die Canvas-Quelle die Nur-Keyframes
              // verursacht. NUR für den Test — Zoom/ROI sind dabei inaktiv.
              let rawCam = false;
              try { rawCam = new URL(window.location.href).searchParams.get('rawcam') === '1'; } catch { /* ignore */ }
              const mainSendTrack = rawCam
                ? (mainPipeline.getRawSourceTrack() ?? mainPipeline.outputTrack!)
                : mainPipeline.outputTrack!;
              if (rawCam) console.warn('[rtc] ?rawcam=1 — DIAGNOSE: sende ROHEN Kamera-Track (kein Canvas/ROI)');
              const mainSender = pc.addTrack(mainSendTrack, mainStream);
              localVideoTracksRef.current.push({
                streamId: mainStream.id, role: 'main', cameraRole: 'webcam', pipeline: mainPipeline, sender: mainSender,
              });
              setLocalStreamMain(mainStream);
              mainCamOk = true;
              console.log('[rtc] Webcam-Pipeline up, output stream', mainStream.id);
            } catch (e) {
              const name = (e as Error).name;
              problems.push(busyName(name) ? 'Webcam belegt/nicht verfügbar (evtl. andere App)' : 'Webcam-Fehler');
              console.warn('[rtc] Webcam konnte nicht geöffnet werden:', name, e);
            }

            // PREVIEW: Doc-Cam
            if (settings.hasPreview && mapping.docCamDeviceId) {
              try {
                const docLabel = videoDevs.find((d) => d.deviceId === mapping.docCamDeviceId)?.label ?? 'Doc-Cam';
                const previewPipeline = new CameraPipeline({
                  cameraRole: 'doccam',
                  deviceId: mapping.docCamDeviceId,
                  label: docLabel,
                  masterTrack: isMasterTrackForRole('doccam', settings),
                  outputWidth: previewRes.width,
                  outputHeight: previewRes.height,
                  outputFps: settings.previewFps,
                  // Welle 8c-pre7: Bandbreite als Ursache widerlegt (native Windows-App
                  // zeigt 3264x2160@15 sauber). Daher native 15 fps für flüssigen Zoom;
                  // das Flackern beheben wir über das Überspringen schwarzer Frames.
                  masterFps: 15,
                });
                await previewPipeline.start();
                pipelinesRef.current.set('doccam', previewPipeline);
                const previewStream = previewPipeline.outputStream!;
                const previewSender = pc.addTrack(previewPipeline.outputTrack!, previewStream);
                localVideoTracksRef.current.push({
                  streamId: previewStream.id, role: 'preview', cameraRole: 'doccam', pipeline: previewPipeline, sender: previewSender,
                });
                setLocalStreamPreview(previewStream);
                setHasPreview(true);
                console.log('[rtc] Doc-Cam-Pipeline up, output stream', previewStream.id);
              } catch (e) {
                console.warn('[rtc] Doc-Cam-Pipeline scheiterte:', e);
                problems.push('Doc-Cam belegt/nicht verfügbar');
              }
            } else if (!settings.hasPreview) {
              console.log('[rtc] single-Profil: keine Doc-Cam');
            }
          }

          // Welle 8c-pre: Geräte-Status aus den TATSÄCHLICHEN Öffnungs-Ergebnissen
          deviceStatusRef.current = {
            hasCamera: mainCamOk, hasMic: audioOk, hasAudioOut,
            message: problems.length ? `Stele: ${problems.join(', ')}` : undefined,
          };
          sendDeviceStatus();
        } else {
          // BERATER: einfache audio+video
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
          if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
          stream.getTracks().forEach((t) => pc.addTrack(t, stream));
          setLocalStreamMain(stream);
        }

        // Welle 7c: SFrame auf alle ausgehenden Sender verdrahten (encrypt).
        if (e2eeRef.current) {
          const sess = e2eeRef.current;
          for (const sender of pc.getSenders()) {
            if (sender.track) applySframe(sender, sess, 'encrypt');
          }
        }

        pc.ontrack = (e) => {
          const stream = e.streams?.[0];
          if (!stream) return;
          console.log('[rtc] ontrack streamId=', stream.id, 'kind=', e.track.kind);
          tuneReceiverForLoss(e.receiver, e.track.kind);
          // Welle 7c: SFrame auf eingehenden Receiver verdrahten (decrypt).
          if (e2eeRef.current) {
            applySframe(e.receiver, e2eeRef.current, 'decrypt');
          }
          remoteStreamsRef.current.set(stream.id, stream);
          refreshRemoteAssignment();
        };
        pc.onicecandidate = (e) => {
          if (e.candidate && socketRef.current) {
            // Welle 8c-pre35: Kandidaten-Typ + relativer Zeitpunkt loggen — macht im
            // Langzeittest sichtbar, wann srflx (Direktpfad) vs. relay verfügbar wird.
            const dt = iceStartRef.current ? Date.now() - iceStartRef.current : 0;
            console.log(`[ice:stele] +${dt}ms ${e.candidate.type ?? '?'} ${e.candidate.protocol ?? ''}`);
            socketRef.current.emit('rtc:ice', { candidate: e.candidate, to: peerIdRef.current });
          }
        };
        // Welle 8c-pre47: als benannte Funktion, damit der F5-Rebuild dieselbe
        // (Stats/Keyframe/Recovery-)Logik 1:1 wiederverwendet — keine Divergenz.
        function onAvStateChange(targetPc: RTCPeerConnection) {
          console.log('[rtc] pc.state', targetPc.connectionState);
          if (targetPc.connectionState === 'connected') {
            const wasConnectedBefore = mediaEverConnectedRef.current;
            mediaEverConnectedRef.current = true;
            if (mediaDeadTimerRef.current) { clearTimeout(mediaDeadTimerRef.current); mediaDeadTimerRef.current = null; }
            setStatus('connected');
            // Welle 8c-pre18: Nach Wiederverbindung frischen Keyframe erzwingen (gegen
            // bleibende Fragmente) — lokaler Encoder + Anforderung an den Berater.
            if (wasConnectedBefore) {
              console.log('[rtc] Wiederverbindung → Keyframe erzwingen (beidseitig)');
              void forceVideoKeyframe(targetPc);
              try { dcRef.current?.send(JSON.stringify({ type: 'camera.keyframe' })); } catch { /* ignore */ }
            }
            if (statsTimerRef.current) clearInterval(statsTimerRef.current);
            const poll = async () => {
              const cur = pcRef.current;
              if (!cur) return;
              const info = await readConnectionInfo(cur);
              info.e2eeActive = e2eeRef.current?.isActive() ?? false;
              info.e2eeIntended = e2eeIntentRef.current;
              info.e2eeSuspended = e2eeRef.current?.isSuspended() ?? false;
              setConnectionInfo(info);
              // Welle 8c-pre16: Freeze-Watchdog. Steht ein eingehender Video-Decoder
              // (Berater-Bild) trotz weiterhin ankommender Pakete, fordern wir aktiv
              // einen Keyframe vom Berater an (camera.keyframe → dort forceVideoKeyframe).
              try {
                const stats = await cur.getStats();
                let stalled = false;
                stats.forEach((r: any) => {
                  if (r.type === 'inbound-rtp' && r.kind === 'video') {
                    const prev = inboundStatsRef.current.get(r.ssrc);
                    const f = r.framesDecoded || 0, p = r.packetsReceived || 0;
                    const pli = r.pliCount || 0, kf = r.keyFramesDecoded || 0;
                    if (prev && f > 0) {
                      const decodeStalled = p > prev.p && f === prev.f;        // friert ein
                      const keyframeStuck = pli > prev.pli && kf === prev.kf;  // bittet um Keyframe, bekommt keinen → korrupt
                      if (decodeStalled || keyframeStuck) stalled = true;
                    }
                    inboundStatsRef.current.set(r.ssrc, { f, p, pli, kf });
                  }
                });
                if (stalled && Date.now() - lastKeyframeReqRef.current > 4000) {
                  lastKeyframeReqRef.current = Date.now();
                  console.warn('[rtc] Decoder steht trotz Datenfluss → Keyframe vom Berater angefordert');
                  try { dcRef.current?.send(JSON.stringify({ type: 'camera.keyframe' })); } catch { /* ignore */ }
                  void forceVideoKeyframe(cur);
                }
                const peerStats = logRtcDiag(stats, getCryptoStats(), diagPrevRef, 'stele', statsAccumRef); // Welle 8c-pre25/31/32
                // Welle 8c-pre32: Default-Verifikation — wenn nicht VP9 ausgehandelt wurde
                // (und ?vp9=0 nicht gesetzt ist), einmalig warnen. Macht ein stilles
                // Zurückfallen auf VP8 (Keyframe-Bug-Risiko) im Langzeittest sichtbar.
                if (peerStats && !codecLockWarnedRef.current && peerStats.codec && peerStats.codec !== '?') {
                  let vp9On = true;
                  try { vp9On = new URL(window.location.href).searchParams.get('vp9') !== '0'; } catch { /* ignore */ }
                  if (vp9On && !/vp9/i.test(peerStats.codec)) {
                    codecLockWarnedRef.current = true;
                    console.warn(`[rtc] ⚠ REGRESSION: Empfangs-Codec ist ${peerStats.codec} statt VP9 — Keyframe-Bug-Risiko bei E2EE!`);
                  } else if (/vp9/i.test(peerStats.codec)) {
                    codecLockWarnedRef.current = true; // VP9 bestätigt, nicht weiter prüfen
                  }
                }
                // Welle 8c-pre31: eigene Kennzahlen P2P an den Berater schicken (cam-ctrl),
                // damit dessen Verbindungsqualitäts-Kachel die Stele-Seite zeigt.
                if (peerStats && dcRef.current?.readyState === 'open') {
                  try { dcRef.current.send(JSON.stringify({ type: 'stats', payload: peerStats })); } catch { /* ignore */ }
                }
              } catch { /* getStats-Fehler ignorieren */ }
            };
            poll();
            statsTimerRef.current = setInterval(poll, 3000);
            // Welle 8c-pre21: alle 8s einen Keyframe erzwingen, solange verbunden. Heilt
            // nicht erkennbare Korruption (z.B. ein korrupt akzeptierter Keyframe) spätestens
            // nach 8s. Intervall bewusst moderat, um den (ggf. knappen) Uplink zu schonen.
            if (kfTimerRef.current) clearInterval(kfTimerRef.current);
            kfTimerRef.current = setInterval(() => {
              const c = pcRef.current;
              if (c && c.connectionState === 'connected') void forceVideoKeyframe(c);
            }, 8000);
          }
          // Welle 8c-pre17: 'disconnected'/'failed' beenden den Call NICHT sofort. Die
          // Stele versucht ICE-Restart (neue Pfad-Aushandlung); transiente Hänger
          // erholt WebRTC ohnehin selbst. Teardown erst nach langer Karenz, wenn sich
          // das Medium nicht erholt — und nur, wenn es vorher überhaupt verbunden war.
          if (targetPc.connectionState === 'disconnected' || targetPc.connectionState === 'failed') {
            recoverMedia(targetPc.connectionState);
            if (!mediaDeadTimerRef.current) {
              mediaDeadTimerRef.current = setTimeout(() => {
                mediaDeadTimerRef.current = null;
                if (pcRef.current?.connectionState !== 'connected') teardown('media-dead');
              }, 25000);
            }
          }
          if (targetPc.connectionState === 'closed') teardown('peer-connection-closed');
        }
        pc.onconnectionstatechange = () => onAvStateChange(pc);

        const socket = io('/rtc', {
          path: '/socket.io', transports: ['websocket'], auth: { token: rtcToken },
        });
        socketRef.current = socket;

        socket.on('connect', () => console.log('[rtc] socket connected'));
        socket.on('connect_error', (e) => {
          console.warn('[rtc] connect_error', e.message);
          setError(`Socket: ${e.message}`);
        });

        function sendTrackMeta() {
          if (role !== 'stele') return;
          const meta: TrackMeta[] = [];
          // Audio-Track (immer da nach Setup)
          if (localAudioTrackRef.current && pc.getSenders().length > 0) {
            // Wir können hier die Stream-ID nicht direkt ermitteln,
            // also markieren wir Audio per Track-Kind beim Empfang (siehe rtc:track-meta-Handler unten).
          }
          // Video: aus den Pipelines
          for (const v of localVideoTracksRef.current) {
            const caps = v.pipeline.getCapabilities();
            meta.push({
              streamId: v.streamId,
              role: v.role,
              cameraRole: v.cameraRole,
              width: caps.outputWidth,
              height: caps.outputHeight,
              frameRate: v.pipeline.outputFps,
            });
          }
          socket.emit('rtc:track-meta', { tracks: meta });
          console.log('[rtc] sent track-meta', JSON.stringify(meta));
        }

        // Welle 7c/7e: eigenen ECDH-Public-Key senden — nur wenn Intent es will.
        const sendE2eeKey = async () => {
          const sess = e2eeRef.current;
          if (!sess || !e2eeIntentRef.current) return;
          try {
            const pub = await sess.exportPublicKey();
            socket.emit('rtc:e2ee-key', { publicKey: pub, keyId: sess.keyId, to: peerIdRef.current });
            console.log('[rtc] E2EE Public Key gesendet');
          } catch (err) {
            console.warn('[rtc] E2EE Public Key senden fehlgeschlagen', err);
          }
        };

        socket.on('rtc:joined', (data) => {
          console.log('[rtc] joined room', JSON.stringify(data));
          // Welle 8c Phase A: A/V-Berater (role=agent, companion=false) und Companion
          // (role=agent, companion=true) auseinanderhalten. Abwärtskompatibel zu reinen
          // String-Peer-IDs (alte Gateway-Version → als A/V behandeln).
          const peers = Array.isArray(data?.peers) ? data.peers : [];
          let avPeerId: string | null = null;
          let compPeerId: string | null = null;
          let guestPeer: { peerId?: string; name?: string } | null = null;
          for (const p of peers) {
            if (typeof p === 'string') { avPeerId = avPeerId ?? p; continue; }
            if (p?.role === 'agent' && p?.companion) compPeerId = p.peerId;
            else if (p?.guest) { guestPeer = p; continue; } // Welle 8d/M2: Experte → separate Strecke
            else avPeerId = avPeerId ?? p?.peerId;
          }
          if (avPeerId) peerIdRef.current = avPeerId;
          if (compPeerId) companionPeerIdRef.current = compPeerId;
          // Welle 7e: Intent server-autoritativ aus rtc:joined; Test-Override via e2eeForced()
          const forced = e2eeForced();
          e2eeIntentRef.current = forced ?? Boolean(data?.e2ee);
          console.log('[rtc] E2EE-Intent =', e2eeIntentRef.current, forced !== null ? '(override)' : '(tenant-policy)');
          sendTrackMeta();
          if (avPeerId) sendE2eeKey();
          // Welle 8c-pre17: Reconnect-Logik.
          //  - Medium gesund → nur Peer-ID re-syncen, NICHT offerieren.
          //  - Medium war verbunden, jetzt gebrochen → ICE-Restart-Offer (Pfad-Recovery).
          //  - Nie verbunden (initial) → normales Offer.
          const connected = pcRef.current?.connectionState === 'connected';
          if (role === 'stele' && avPeerId && !connected) {
            if (mediaEverConnectedRef.current) recoverMedia('reconnect'); // entprellter ICE-Restart
            else void makeOffer(false); // initiales Offer
          } else if (role === 'agent' && !connected) setStatus('waiting-offer');
          // Welle 8d/M2: War beim (Re-)Join schon ein Experte im Raum → ihm offerieren.
          if (role === 'stele' && guestPeer?.peerId && guestPeer.peerId !== guestPeerIdRef.current) {
            void makeGuestOffer(guestPeer.peerId, guestPeer.name);
          }
        });

        socket.on('rtc:peer-joined', async (data: { peerId?: string; role?: string; companion?: boolean; guest?: boolean; name?: string }) => {
          console.log('[rtc] peer joined', JSON.stringify(data));
          // Welle 8d / Mesh M2: Experte (Magic-Link-Gast) → die Stele offeriert ihm eine
          // eigene Strecke (Audio + aktiver Haupt-Kamera-Track). pcRef (A/V-Berater)
          // bleibt unangetastet; peerIdRef wird NICHT auf den Gast gesetzt.
          if (data?.guest) {
            console.log('[rtc] Experte erkannt → Stele-Offer:', data.peerId, data.name);
            if (data.peerId) void makeGuestOffer(data.peerId, data.name);
            return;
          }
          // Companion: nur merken und auf sein Offer warten — pcRef (A/V) NICHT anfassen.
          if (data?.role === 'agent' && data?.companion) {
            if (data.peerId) companionPeerIdRef.current = data.peerId;
            console.log('[rtc] Companion-Peer erkannt, warte auf Offer:', data.peerId);
            return;
          }
          // A/V-Berater (oder legacy ohne role): wie bisher.
          // Welle 9: Namen des verbundenen Beraters für die Anzeige übernehmen.
          if (data?.role === 'agent' && data?.name) {
            try { useSteleStore.getState().setConnectedAgentName(data.name); } catch { /* */ }
          }
          // Welle 8c-pre18: War eine Reconnect-Karenz aktiv (transientes bye = Reload),
          // erzwingen wir Recovery, auch wenn pc noch (veraltet) 'connected' meldet.
          const hadByeGrace = !!byeGraceTimerRef.current;
          if (byeGraceTimerRef.current) { clearTimeout(byeGraceTimerRef.current); byeGraceTimerRef.current = null; }
          if (data?.peerId) peerIdRef.current = data.peerId;
          sendTrackMeta();
          sendE2eeKey();
          const connected = pcRef.current?.connectionState === 'connected';
          if (role === 'stele') {
            // Welle 8c-pre47 (F5-Restore): hadByeGrace = der Berater hat vor dem Reload ein
            // transientes bye gesendet → er kehrt mit einer FABRIKNEUEN PeerConnection zurück.
            // Dann KOMPLETT neu aufbauen (frisches Offer + frisches SFrame), nicht nur ICE
            // neu starten. Gated über ?f5restore=0 (Rückfall auf das alte Reconnect-Verhalten).
            let f5restore = true;
            try { f5restore = new URL(window.location.href).searchParams.get('f5restore') !== '0'; } catch { /* ignore */ }
            if (!mediaEverConnectedRef.current) void makeOffer(false);            // initial
            else if (hadByeGrace && f5restore) void rebuildAvPeer();              // Berater-F5 → frische PC
            else if (!connected || hadByeGrace) recoverMedia('rejoin');          // Blip → ICE-Restart
            // sonst: gesunder Socket-Reconnect → nur Peer-ID/E2EE re-syncen
          }
        });

        // Welle 7c/7e: Peer-Public-Key empfangen → Shared Key ableiten, nur wenn Intent an
        socket.on('rtc:e2ee-key', async (data: { publicKey: string; keyId: number; from?: string }) => {
          if (!e2eeIntentRef.current) return;
          // Welle 8c Phase A: nur der A/V-Berater hat einen SFrame-Handshake; alles
          // andere (Companion) ignorieren.
          if (data.from && data.from !== peerIdRef.current) return;
          if (data.from) peerIdRef.current = data.from;
          console.log('[rtc] E2EE Peer-Key empfangen');
          await e2eeRef.current?.onPeerKey(data.publicKey);
          if (e2eeRef.current?.hasKey()) socket.emit('rtc:e2ee-ready', { to: peerIdRef.current });
        });

        // Welle 7e: Peer bestätigt Schlüssel → wir dürfen verschlüsseln (nie vorher)
        socket.on('rtc:e2ee-ready', () => {
          if (!e2eeIntentRef.current) return;
          e2eeRef.current?.markPeerReady();
        });

        // Welle 7e: Teilnehmerzahl-Wechsel → E2EE nahtlos pausieren/fortsetzen.
        socket.on('rtc:e2ee-mode', (data: { active: boolean; participants: number }) => {
          console.log('[rtc] E2EE-Modus:', data.active ? 'aktiv' : 'pausiert', `(${data.participants} Teilnehmer)`);
          e2eeRef.current?.setMode(data.active);
        });

        // Welle 7e: Aufzeichnungs-Status der Gegenseite
        socket.on('rtc:recording-state', (data: { active: boolean; topic?: string }) => {
          console.log('[rtc] recording-state', data.active);
          setRemoteRecording({ active: data.active, topic: data.topic });
        });

        // Welle 7e/pre97: Berater fordert aktive Zustimmung an → Consent-Sheet anzeigen.
        // Die Touch-Entscheidung des Gastes wird zurückgemeldet (keine mündliche Zustimmung).
        socket.on('rtc:recording-consent-request', (data: { topic?: string; mode?: string }) => {
          console.log('[rtc] recording-consent-request', data.topic);
          try {
            useSteleStore.getState().openConsent(data.topic, (accepted: boolean) => {
              socket.emit('rtc:recording-consent-response', { accepted, at: Date.now() });
            });
          } catch { /* */ }
        });

        // Welle 7e/pre98: Berater hat die Anfrage abgebrochen → Sheet schließen + kurzer Hinweis.
        socket.on('rtc:recording-consent-cancel', () => {
          console.log('[rtc] recording-consent-cancel');
          try {
            const st = useSteleStore.getState();
            st.closeConsent();
            st.setConsentNotice('cancelled');
            setTimeout(() => { try { useSteleStore.getState().setConsentNotice(null); } catch { /* */ } }, 4000);
          } catch { /* */ }
        });

        socket.on('rtc:offer', async (data: { sdp: any; from?: string }) => {
          console.log('[rtc] received offer from', data.from);
          // Welle 8c Phase A: Die Stele offert IMMER selbst zum A/V-Berater und
          // ANTWORTET nur dem Companion. Ein eingehendes Offer stammt daher vom
          // Companion (robust auch ohne vorher verarbeitetes peer-joined).
          if (data.from && data.from !== peerIdRef.current) {
            await setupCompanionAnswer(data.sdp, data.from);
            return;
          }
          if (data.from) peerIdRef.current = data.from;
          setStatus('negotiating');
          // Welle 8c-pre48: IMMER die aktive PC (pcRef.current) verwenden — nach einem
          // F5-Rebuild zeigt die Closure-Variable `pc` auf die alte, geschlossene PC.
          const curPc = pcRef.current;
          if (!curPc) return;
          await curPc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          for (const c of pendingIceRef.current) {
            await curPc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
          }
          pendingIceRef.current = [];
          const answer = await curPc.createAnswer();
          await curPc.setLocalDescription(answer);
          socket.emit('rtc:answer', { sdp: curPc.localDescription, to: peerIdRef.current });
        });

        socket.on('rtc:answer', async (data: { sdp: any; from?: string }) => {
          console.log('[rtc] received answer');
          // Welle 8d/M2: Answer des Experten auf unser Stele-Offer → guestPc (nicht pcRef!).
          if (data.from && data.from === guestPeerIdRef.current) {
            const gpc = guestPcRef.current;
            if (!gpc) return;
            await gpc.setRemoteDescription(new RTCSessionDescription(data.sdp));
            for (const c of guestPendingIceRef.current) await gpc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
            guestPendingIceRef.current = [];
            console.log('[rtc][mesh] Experten-Answer (Stele) verarbeitet');
            return;
          }
          if (data.from) peerIdRef.current = data.from;
          // Welle 8c-pre48: aktive PC verwenden — nach F5-Rebuild ist die Antwort für npc,
          // nicht für die alte (geschlossene) Closure-`pc`. Das war die Ursache des
          // „signalingState is 'closed'"-Fehlers beim F5-Restore.
          const curPc = pcRef.current;
          if (!curPc) return;
          if (curPc.signalingState === 'stable') {
            console.warn('[rtc] Answer verworfen — PC bereits stable (verspätet/doppelt)');
            return;
          }
          await curPc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          for (const c of pendingIceRef.current) {
            await curPc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
          }
          pendingIceRef.current = [];
        });

        socket.on('rtc:ice', async (data) => {
          // Welle 8d/M2: ICE des Experten → guestPc (eigener Trickle-Puffer).
          if (data.from && data.from === guestPeerIdRef.current) {
            const gpc = guestPcRef.current;
            if (!gpc || !gpc.remoteDescription) { guestPendingIceRef.current.push(data.candidate); return; }
            await gpc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch((e) =>
              console.warn('[rtc][mesh] Experten-addIceCandidate (Stele) failed', e),
            );
            return;
          }
          // Welle 8c Phase A: ICE, dessen Quelle nicht der A/V-Berater ist, gehört
          // zum Companion → companionPc (eigener Puffer, robust gegen Trickle-Timing).
          if (data.from && data.from !== peerIdRef.current) {
            if (!companionPeerIdRef.current) companionPeerIdRef.current = data.from;
            const cpc = companionPcRef.current;
            if (!cpc || !cpc.remoteDescription) { companionPendingIceRef.current.push(data.candidate); return; }
            await cpc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch((e) =>
              console.warn('[rtc][companion] addIceCandidate failed', e),
            );
            return;
          }
          // Welle 8c-pre48: aktive PC verwenden (nach F5-Rebuild = npc).
          const curPc = pcRef.current;
          if (!curPc || !curPc.remoteDescription) {
            pendingIceRef.current.push(data.candidate);
            return;
          }
          await curPc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch((e) =>
            console.warn('[rtc] addIceCandidate failed', e),
          );
        });

        socket.on('rtc:peer-left', (data: { peerId?: string; role?: string }) => {
          if (data?.peerId && data.peerId === companionPeerIdRef.current) { closeCompanion(); return; }
          // Welle 8d/M2: Experte verlässt den Raum → nur dessen Strecke schließen.
          if (data?.peerId && data.peerId === guestPeerIdRef.current) { closeGuest(); return; }
          // Welle 8c-pre17: Ein Socket-Abbruch des Beraters beendet den Call NICHT. Das
          // P2P-Medium läuft weiter; Socket.IO verbindet neu (→ rtc:peer-joined re-synct
          // die Peer-ID). Nur ein bewusstes Auflegen (rtc:bye) oder echter Medien-Tod
          // (connectionState, mediaDeadTimer) beendet den Call.
          console.log('[rtc] peer-left (Socket-Drop) — Medium entscheidet, kein Teardown');
        });
        socket.on('rtc:bye', (data: { from?: string; transient?: boolean }) => {
          if (data?.from && data.from === companionPeerIdRef.current) { closeCompanion(); return; }
          // Welle 8d/M2: bye des Experten → dessen Strecke schließen (Tile räumen).
          if (data?.from && data.from === guestPeerIdRef.current) { closeGuest(); return; }
          // Welle 8d/M1: bye eines Experten (Magic-Link-Gast) hat from=Gast-ID ≠ A/V-Berater.
          // NUR ein bye des A/V-Beraters (oder ein legacy-bye ohne from) beendet den Call —
          // sonst reißt ein auflegender Experte den ganzen Stele↔Berater-Call ab.
          if (data?.from && data.from !== peerIdRef.current) {
            console.log('[rtc] bye eines Nicht-A/V-Peers (Experte), ignoriere:', data.from);
            return;
          }
          // Welle 8c-pre18: transientes bye (Reload/Unload des Beraters) → Reconnect-Karenz
          // statt Sofort-Ende; peer-joined löscht sie. Hartes bye (Auflegen) → sofort beenden.
          if (data?.transient) {
            console.log('[rtc] transientes bye — 12s Reconnect-Karenz');
            if (!byeGraceTimerRef.current) {
              byeGraceTimerRef.current = setTimeout(() => {
                byeGraceTimerRef.current = null;
                if (pcRef.current?.connectionState !== 'connected') teardown('peer-bye-timeout');
              }, 12000);
            }
            return;
          }
          teardown('peer-bye');
        });

        socket.on('rtc:track-meta', (data: { tracks: TrackMeta[] }) => {
          console.log('[rtc] received track-meta', JSON.stringify(data));
          remoteMetaRef.current.clear();
          for (const t of data.tracks) remoteMetaRef.current.set(t.streamId, t);
          // Audio-Tracks markieren über den Track-Kind beim Empfang:
          remoteStreamsRef.current.forEach((s, id) => {
            if (!remoteMetaRef.current.has(id)) {
              const hasAudio = s.getAudioTracks().length > 0;
              const hasVideo = s.getVideoTracks().length > 0;
              if (hasAudio && !hasVideo) {
                remoteMetaRef.current.set(id, { streamId: id, role: 'audio' });
              }
            }
          });
          refreshRemoteAssignment();
        });

        socket.on('rtc:swap', async (payload?: { followMesh?: boolean }) => {
          const followMesh = !!payload?.followMesh;
          console.log(`[rtc] swap: Mesh-Mitnahme ${followMesh ? 'AN' : 'aus'} (payload=${JSON.stringify(payload ?? null)})`);
          if (role !== 'stele') return;
          console.log('[rtc] received swap request');
          const mainInfo = localVideoTracksRef.current.find((t) => t.role === 'main');
          const previewInfo = localVideoTracksRef.current.find((t) => t.role === 'preview');
          if (!mainInfo || !previewInfo) {
            console.warn('[rtc] swap nicht möglich');
            return;
          }
          const s = settingsRef.current;
          const mainRes = RESOLUTIONS[s.mainResolution] ?? RESOLUTIONS['720p'];
          const previewRes = RESOLUTIONS[s.previewResolution] ?? RESOLUTIONS['240p'];

          // Rollen tauschen
          const oldMainPipe = mainInfo.pipeline;
          const oldPreviewPipe = previewInfo.pipeline;
          // Canvas-Output-Auflösungen tauschen
          oldMainPipe.setOutputResolution(previewRes.width, previewRes.height, s.previewFps);
          oldPreviewPipe.setOutputResolution(mainRes.width, mainRes.height, s.mainFps);

          mainInfo.role = 'preview';
          previewInfo.role = 'main';
          sendTrackMeta();
          // ROI bleibt pro Pipeline erhalten - jeder Kamera ihr eigener ROI
          // Berater muss auch seine Capability-Anzeige updaten:
          sendCapabilities();

          // Welle 8d/M2: Ist die Vorschau abgeschaltet (Bandbreite gespart), trotzdem
          // Umschalten ermöglichen: der NEUE main-Sender wird (re)aktiviert, der NEUE
          // preview-Sender (die alte Hauptkamera) abgeschaltet. So wechselt der Berater
          // die Hauptkamera, ohne dass je zwei Streams gleichzeitig laufen.
          if (!previewEnabledRef.current) {
            const nm = localVideoTracksRef.current.find((t) => t.role === 'main');
            const np = localVideoTracksRef.current.find((t) => t.role === 'preview');
            if (nm?.sender && nm.pipeline?.outputTrack) void nm.sender.replaceTrack(nm.pipeline.outputTrack);
            if (np?.sender) void np.sender.replaceTrack(null);
          }

          // Welle 8d/M2: Experten-Strecke folgt dem Swap. Der guestPc sendet nur EINEN
          // Video-Track (den main-Track zum Offer-Zeitpunkt). Nach dem Rollentausch muss
          // der Sender per replaceTrack auf den NEUEN main-Track (z.B. Doc-Cam) umgezogen
          // werden — sonst sieht der Experte weiter die alte Kamera (in Preview-Auflösung),
          // während der Berater längst das Dokument zeigt.
          const gpc = guestPcRef.current;
          if (gpc) {
            const newMain = localVideoTracksRef.current.find((t) => t.role === 'main');
            const newVt = newMain?.pipeline?.outputTrack;
            const vSender = gpc.getSenders().find((sn) => sn.track?.kind === 'video');
            if (followMesh && vSender && newVt && vSender.track !== newVt) {
              try {
                await vSender.replaceTrack(newVt);
                console.log('[rtc][mesh] Experten-Video folgt Swap → neuer main-Track');
              } catch (e) {
                console.warn('[rtc][mesh] replaceTrack (Experte) fehlgeschlagen', e);
              }
            }
          }
          console.log('[rtc] swap durchgeführt');
        });

        async function makeOffer(iceRestart = false) {
          if (!pcRef.current) return;
          if (!iceRestart) setStatus('negotiating');
          preferVp9(pcRef.current); // Welle 8c-pre29: VP9 bevorzugen (vor createOffer)
          const offer = await pcRef.current.createOffer(iceRestart ? { iceRestart: true } : undefined);
          // Welle 8c-pre28: DD-Extension entfernen (außer ?dd=keep). Behebt den
          // versions-paarungs-abhängigen Nur-Keyframe-Modus mit aktivem E2EE-Transform.
          let ddKeep = false;
          try { ddKeep = new URL(window.location.href).searchParams.get('dd') === 'keep'; } catch { /* ignore */ }
          if (!ddKeep && offer.sdp) {
            const stripped = stripDependencyDescriptor(offer.sdp);
            if (stripped !== offer.sdp) console.log('[rtc] Dependency-Descriptor-Extension aus Offer entfernt (festgezurrter Default)');
            await pcRef.current.setLocalDescription({ type: 'offer', sdp: stripped });
          } else {
            await pcRef.current.setLocalDescription(offer);
          }
          // Welle 8c-pre32: Default verifizieren — WARNT, falls DD trotz Strip im SDP bleibt.
          assertNoDependencyDescriptor(pcRef.current.localDescription?.sdp, !ddKeep, 'Offer');
          socket.emit('rtc:offer', { sdp: pcRef.current.localDescription, to: peerIdRef.current });
        }

        // ── Welle 8d / Mesh M2: Stele→Experte (Stele ist OFFERER, der Gast antwortet) ──
        // Eigene PeerConnection, getrennt vom A/V-pcRef. Transport-verschlüsselt, KEIN
        // SFrame und KEIN encodedInsertableStreams (sonst stocken die Frames, vgl. pre54).
        async function makeGuestOffer(guestId: string, name?: string) {
          try {
            if (guestPcRef.current) { try { guestPcRef.current.close(); } catch { /* ignore */ } }
            const gpc = new RTCPeerConnection({ iceServers: pcConfig.iceServers, iceCandidatePoolSize: 2 });
            guestPcRef.current = gpc;
            guestPeerIdRef.current = guestId;
            guestPendingIceRef.current = [];
            if (name) setExpertName(name);

            // Kunde → Experte: Audio + EIN aktiver Haupt-Kamera-Track (M2-Festlegung).
            const at = localAudioTrackRef.current;
            if (at && at.readyState === 'live') gpc.addTrack(at, new MediaStream([at]));
            const mainV = localVideoTracksRef.current.find((v) => v.role === 'main') ?? localVideoTracksRef.current[0];
            const vt = mainV?.pipeline?.outputTrack;
            const vs = mainV?.pipeline?.outputStream;
            if (vt && vs) gpc.addTrack(vt, vs);

            gpc.ontrack = (e) => {
              const stream = e.streams[0] || new MediaStream([e.track]);
              console.log('[rtc][mesh] Experte ontrack', e.track.kind, 'muted=', e.track.muted);
              setRemoteExpertStream(stream);
            };
            gpc.onicecandidate = (e) => {
              if (e.candidate) socketRef.current?.emit('rtc:ice', { candidate: e.candidate, to: guestId });
            };
            gpc.onconnectionstatechange = () => {
              const st = gpc.connectionState;
              console.log('[rtc][mesh] Experten-PC (Stele) state', st);
              if (st === 'failed' || st === 'closed') setRemoteExpertStream(null);
            };

            const offer = await gpc.createOffer();
            await gpc.setLocalDescription(offer);
            socketRef.current?.emit('rtc:offer', { sdp: gpc.localDescription, to: guestId });
            console.log('[rtc][mesh] Stele-Offer an Experten gesendet', guestId);
          } catch (e) {
            console.warn('[rtc][mesh] makeGuestOffer (Stele) fehlgeschlagen', e);
          }
        }

        function closeGuest() {
          try { guestPcRef.current?.close(); } catch { /* ignore */ }
          guestPcRef.current = null;
          guestPeerIdRef.current = null;
          guestPendingIceRef.current = [];
          setRemoteExpertStream(null);
          setExpertName(null);
          console.log('[rtc][mesh] Experten-Strecke (Stele) geschlossen');
        }

        // Welle 8c-pre17: Medien-Recovery. Bei brüchigem/gebrochenem Medium versucht die
        // Stele (Offerer) einen ICE-Restart (neue Pfad-Aushandlung), statt sofort
        // abzubrechen. Debounced; greift nur wenn ein Peer und ein Socket da sind.
        function recoverMedia(reason: string) {
          const now = Date.now();
          if (now - lastIceRestartRef.current < 4000) return;
          if (!peerIdRef.current || !socketRef.current?.connected) return;
          lastIceRestartRef.current = now;
          console.warn(`[rtc] Medien-Recovery: ICE-Restart (${reason})`);
          void makeOffer(true);
        }

        // ── Welle 8c-pre47 (F5-Restore) ───────────────────────────────────────────
        // Baut die A/V-PeerConnection NEU auf, wenn der Berater die App neu lädt (F5).
        // Seine PeerConnection ist danach fabrikneu (neues DTLS + neues SFrame); ein
        // ICE-Restart der ALTEN Stele-PC würde nicht greifen (DTLS passt nicht). Audio
        // und Kamera-Pipelines bleiben offen (in Refs) und werden 1:1 wiederverwendet —
        // also KEIN Neuöffnen der Kameras (Master-Track-Prinzip). DataChannel- und
        // Connection-State-Logik kommen aus den geteilten Funktionen (keine Divergenz).
        async function rebuildAvPeer() {
          if (role !== 'stele') return;
          console.warn('[rtc][f5] Berater-Reload → A/V-PeerConnection neu aufbauen (Kameras bleiben offen)');
          try {
            try { dcRef.current?.close(); } catch { /* ignore */ }
            dcRef.current = null;
            // WICHTIG: Handler der alten PC abhängen, BEVOR wir sie schließen — sonst
            // feuert deren onconnectionstatechange('closed') → onAvStateChange → teardown
            // und würde den Call mitten im Rebuild abräumen.
            const oldPc = pcRef.current;
            if (oldPc) {
              oldPc.onconnectionstatechange = null;
              oldPc.ontrack = null;
              oldPc.onicecandidate = null;
              try { oldPc.close(); } catch { /* ignore */ }
            }
            pcRef.current = null;
            if (mediaDeadTimerRef.current) { clearTimeout(mediaDeadTimerRef.current); mediaDeadTimerRef.current = null; }
            if (statsTimerRef.current) { clearInterval(statsTimerRef.current); statsTimerRef.current = null; }
            if (kfTimerRef.current) { clearInterval(kfTimerRef.current); kfTimerRef.current = null; }

            // Frische E2EE-Session (neuer Berater = neuer ECDH-Schlüssel)
            e2eeRef.current = null;
            if (sframeSupported()) {
              try { e2eeRef.current = await createE2eeSession(); }
              catch (err) { console.warn('[rtc][f5] E2EE-Neuaufbau fehlgeschlagen, ohne SFrame', err); e2eeRef.current = null; }
            }

            const npc = new RTCPeerConnection(pcConfig);
            iceStartRef.current = Date.now();
            pcRef.current = npc;
            mediaEverConnectedRef.current = false; // wie Erstverbindung → initiales Offer
            pendingIceRef.current = []; // Welle 8c-pre48: alte Trickle-Kandidaten verwerfen

            wireCamDataChannel(npc);

            // Bestehendes Audio wiederverwenden (NICHT neu öffnen)
            const at = localAudioTrackRef.current;
            if (at && at.readyState === 'live') npc.addTrack(at, new MediaStream([at]));
            // Bestehende Kamera-Pipeline-Tracks wiederverwenden
            for (const v of localVideoTracksRef.current) {
              const t = v.pipeline?.outputTrack;
              const s = v.pipeline?.outputStream;
              if (t && s) npc.addTrack(t, s);
            }

            // SFrame-Encrypt auf die neuen Sender
            if (e2eeRef.current) {
              for (const sender of npc.getSenders()) if (sender.track) applySframe(sender, e2eeRef.current, 'encrypt');
            }

            npc.ontrack = (e) => {
              const stream = e.streams?.[0];
              if (!stream) return;
              console.log('[rtc] ontrack (rebuild) streamId=', stream.id, 'kind=', e.track.kind);
              tuneReceiverForLoss(e.receiver, e.track.kind);
              if (e2eeRef.current) applySframe(e.receiver, e2eeRef.current, 'decrypt');
              remoteStreamsRef.current.set(stream.id, stream);
              refreshRemoteAssignment();
            };
            npc.onicecandidate = (e) => {
              if (e.candidate && socketRef.current) {
                const dt = iceStartRef.current ? Date.now() - iceStartRef.current : 0;
                console.log(`[ice:stele] +${dt}ms ${e.candidate.type ?? '?'} ${e.candidate.protocol ?? ''} (rebuild)`);
                socketRef.current.emit('rtc:ice', { candidate: e.candidate, to: peerIdRef.current });
              }
            };
            npc.onconnectionstatechange = () => onAvStateChange(npc);

            sendTrackMeta();
            if (e2eeRef.current) sendE2eeKey();
            void makeOffer(false);
            console.log('[rtc][f5] frisches Offer nach Berater-Reload gesendet');
          } catch (e) {
            console.warn('[rtc][f5] Rebuild fehlgeschlagen', e);
          }
        }

        // ── Welle 8c Phase A: Companion-Pfad (Stele = antwortende Seite) ───────────
        // Der Companion (zweite Berater-Session, kein A/V) offert; die Stele baut eine
        // SEPARATE PeerConnection auf — pcRef (A/V) bleibt unangetastet. Steuerkanal
        // über DTLS P2P. Phase B ergänzt hier den eingehenden Screen-Track → Gast.
        async function setupCompanionAnswer(offerSdp: any, fromId: string) {
          companionPeerIdRef.current = fromId;
          let cpc = companionPcRef.current;
          if (!cpc) {
            cpc = new RTCPeerConnection(pcConfig);
            companionPcRef.current = cpc;
            // Welle 8c Phase B: Screen-E2EE-Session vorab anlegen (wenn E2EE für den Call
            // aktiv), damit ontrack und der Steuerkanal-Handshake sie synchron nutzen.
            if (e2eeIntentRef.current && sframeSupported() && !companionScreenE2eeRef.current) {
              try { companionScreenE2eeRef.current = await createE2eeSession('companion-screen'); }
              catch (err) { console.warn('[rtc][companion] Screen-E2EE-Setup fehlgeschlagen', err); }
            }
            cpc.onicecandidate = (e) => {
              if (e.candidate) {
                socketRef.current?.emit('rtc:ice', { candidate: e.candidate, to: companionPeerIdRef.current });
              }
            };
            cpc.ondatachannel = (e) => {
              const dc = e.channel;
              companionDcRef.current = dc;
              dc.binaryType = 'arraybuffer'; // Welle 8c-pre19: für Scan-Chunks
              dc.onopen = () => {
                console.log('[rtc][companion] Steuerkanal offen');
                try { dc.send(JSON.stringify({ type: 'companion.hello', from: 'stele' })); } catch { /* ignore */ }
              };
              dc.onclose = () => console.log('[rtc][companion] Steuerkanal geschlossen');
              dc.onerror = (err) => console.warn('[rtc][companion] dc error', err);
              dc.onmessage = async (ev) => {
                // Welle 12 (pre129): Binäre Daten = PDF-Chunk für einen Signier-Job.
                // Der Companion-Kanal muss sie — wie der A/V-Kanal — in den Signier-Puffer
                // legen, sonst bleibt der Upload „unvollständig" und es kommt kein Ack zurück.
                if (typeof ev.data !== 'string') {
                  const buf: ArrayBuffer = ev.data instanceof ArrayBuffer ? ev.data : await (ev.data as Blob).arrayBuffer();
                  const sb = sigInBufferRef.current;
                  if (sb) {
                    sb.chunks.push(buf);
                    sb.receivedBytes += buf.byteLength;
                    const pct = sb.totalBytes > 0 ? Math.min(100, Math.round((sb.receivedBytes / sb.totalBytes) * 100)) : 0;
                    window.dispatchEvent(new CustomEvent('bamoa:sign-progress', { detail: { phase: 'receiving', pct } }));
                  }
                  return;
                }
                let msg: any;
                try { msg = JSON.parse(ev.data); } catch { return; /* Rohdaten ignorieren */ }
                console.log('[rtc][companion] msg', msg?.type);
                // Welle 8c Phase B: eigener SFrame-Handshake für den Companion-Screen,
                // ausschließlich über diesen P2P-Steuerkanal (Schlüssel nie über Server).
                if (msg?.type === 'companion.e2ee-key' && msg.publicKey) {
                  const sess = companionScreenE2eeRef.current;
                  if (sess) {
                    await sess.onPeerKey(msg.publicKey);
                    try {
                      dc.send(JSON.stringify({ type: 'companion.e2ee-key', publicKey: await sess.exportPublicKey() }));
                      dc.send(JSON.stringify({ type: 'companion.e2ee-ready' }));
                    } catch { /* ignore */ }
                  }
                  return;
                }
                if (msg?.type === 'companion.e2ee-ready') {
                  companionScreenE2eeRef.current?.markPeerReady(); // Stele entschlüsselt nur; harmlos
                  return;
                }
                // Arbitrierung: Companion meldet Start/Ende der Bildschirmfreigabe.
                if (msg?.type === 'companion.share-state') {
                  if (!msg.active) setCompanionScreenStream(null);
                  // Welle 8c-pre21: Der Wechsel Screen-Share an/aus (Companion-pc-Renegotiation)
                  // kann das A/V-Video kurz stören und die Korruption tritt teils ~1s verzögert
                  // auf. Mehrere A/V-Keyframes über die nächsten Sekunden bracketen das Fenster
                  // (lokal → Berater-Decoder, camera.keyframe → Stele-Decoder). Der periodische
                  // 8s-Keyframe fängt alles Spätere ohnehin ab.
                  for (const delay of [300, 1500, 3500]) {
                    setTimeout(() => {
                      const c = pcRef.current;
                      if (c && c.connectionState === 'connected') void forceVideoKeyframe(c);
                      try { dcRef.current?.send(JSON.stringify({ type: 'camera.keyframe' })); } catch { /* ignore */ }
                    }, delay);
                  }
                  return;
                }
                // Welle 8c-pre19 (Phase C) / Welle 12 (pre117): Kamera-, Licht- und
                // Signatur-Befehle des Companion wie beim A/V-Berater behandeln — Antworten
                // (Scan-Chunks, roi.updated, light.state, sign.*) gehen über DIESEN Kanal zurück.
                if (typeof msg?.type === 'string'
                  && (msg.type.startsWith('camera.') || msg.type === 'light.set' || msg.type.startsWith('sign.'))) {
                  handleCamCommand(msg as CamCommand, dc);
                  return;
                }
              };
            };
            cpc.ontrack = (e) => {
              // Welle 8c Phase B: Screen-Share-Track des Companion → Gast-Hauptbild.
              console.log('[rtc][companion] ontrack kind=', e.track.kind);
              if (e.track.kind !== 'video') return;
              tuneReceiverForLoss(e.receiver, e.track.kind);
              const stream = e.streams?.[0] ?? new MediaStream([e.track]);
              // Entschlüsseln, sofern E2EE für den Call aktiv ist (eigene Companion-Session).
              if (e2eeIntentRef.current) {
                const sess = companionScreenE2eeRef.current;
                if (sess) applySframe(e.receiver, sess, 'decrypt');
              }
              e.track.onended = () => setCompanionScreenStream((cur) => (cur === stream ? null : cur));
              setCompanionScreenStream(stream);
            };
            cpc.onconnectionstatechange = () => {
              const st = companionPcRef.current?.connectionState;
              console.log('[rtc][companion] state=', st);
              if (st === 'failed' || st === 'closed') closeCompanion();
            };
            console.log('[rtc][companion] PeerConnection angelegt für', fromId);
          }
          await cpc.setRemoteDescription(new RTCSessionDescription(offerSdp));
          for (const c of companionPendingIceRef.current) {
            await cpc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
          }
          companionPendingIceRef.current = [];
          const answer = await cpc.createAnswer();
          await cpc.setLocalDescription(answer);
          socketRef.current?.emit('rtc:answer', { sdp: cpc.localDescription, to: fromId });
          console.log('[rtc][companion] Offer beantwortet →', fromId);
        }

        function closeCompanion() {
          try { companionDcRef.current?.close(); } catch { /* ignore */ }
          companionDcRef.current = null;
          try { companionPcRef.current?.close(); } catch { /* ignore */ }
          companionPcRef.current = null;
          companionPeerIdRef.current = null;
          companionPendingIceRef.current = [];
          companionScreenE2eeRef.current = null;
          setCompanionScreenStream(null);
          console.log('[rtc][companion] Pfad geschlossen (A/V unberührt)');
        }
      } catch (e: any) {
        console.error('[rtc] setup failed', e);
        setError(e?.message ?? 'WebRTC-Setup fehlgeschlagen');
        setStatus('error');
      }
    })();

    const onBeforeUnload = () => {
      // Welle 8c-pre18: Reload/Unload → transientes bye (Reconnect-Karenz statt Sofort-Ende).
      try { socketRef.current?.emit('rtc:bye', { transient: true }); } catch {}
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);

    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
      try { socketRef.current?.emit('rtc:bye'); } catch {}
      try { dcRef.current?.close(); } catch {}
      dcRef.current = null;
      try { socketRef.current?.disconnect(); } catch {}
      socketRef.current = null;
      try { pcRef.current?.close(); } catch {}
      pcRef.current = null;
      try { companionDcRef.current?.close(); } catch {}
      companionDcRef.current = null;
      try { companionPcRef.current?.close(); } catch {}
      companionPcRef.current = null;
      companionPeerIdRef.current = null;
      try { localAudioTrackRef.current?.stop(); } catch {}
      localAudioTrackRef.current = null;
      pipelinesRef.current.forEach((p) => p.stop());
      pipelinesRef.current.clear();
      localVideoTracksRef.current = [];
    };
  }, [rtcToken, role, steleId]);

  // Welle 8c-pre38: Signier-Job auf der Stele. Nimmt das empfangene PDF, ruft die lokale
  // SignController-API (POST /Sign/PostFileBase64, redirect:follow → getPdfAsync blockiert
  // bis zur Unterschrift am Signotec-Delta-Display, bis ~5 min), und sendet das signierte
  // PDF gechunkt an den Berater zurück. Während des Wartens zeigt die Stele die Anleitung.
  async function runSigningJob(
    sigId: string, filename: string, pdfBytes: Uint8Array, replyDc: RTCDataChannel | null,
  ) {
    const dc = replyDc ?? dcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    sigJobRef.current = sigId;
    // PDF → base64 (robust via FileReader, auch für mehrere MB)
    const b64: string = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve((fr.result as string).replace(/^data:[^,]*,/, ''));
      fr.onerror = reject;
      fr.readAsDataURL(new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' }));
    });
    window.dispatchEvent(new CustomEvent('bamoa:sign-instructions', { detail: { show: true } }));
    try { dc.send(JSON.stringify({ type: 'sign.received', sigId } as CamEvent)); } catch { /* ignore */ }
    try { dc.send(JSON.stringify({ type: 'sign.signing', sigId } as CamEvent)); } catch { /* ignore */ }

    const ctrl = new AbortController();
    sigAbortRef.current = ctrl;
    const timeout = window.setTimeout(() => { try { ctrl.abort(); } catch { /* */ } }, 7 * 60 * 1000);
    const base = (window as any).__BAMOA_SIGN_BASE__ || 'http://127.0.0.1:8080';
    // Eindeutiger Dateiname für die lokale API (verhindert Kollisionen im Data-Verzeichnis
    // bei Ersatz-/Mehrfach-Signaturen). Der Anzeigename beim Berater bleibt 'filename'.
    const apiFilename = `${sigId}_${filename}`.replace(/[^\w.\-]/g, '_');
    try {
      // Welle 8c-pre42: KEIN getkill mehr VOR dem Post. Der Pre-Reset (pre39) reinitialisierte
      // signoSign2 unmittelbar vor der Nutzung und ließ die lokale API auch beim 1. Dokument
      // mit HTTP 500 scheitern. Stattdessen: direkt posten; NUR bei Fehler getkill + 1× Retry.
      // Nach Erfolg wird signoSign2 fürs NÄCHSTE Dokument aufgeräumt (deferred cleanup), damit
      // der frühere „2. Dokument hängt"-Fall ohne Fehlversuch vermieden wird.
      let out: Uint8Array<ArrayBuffer>;
      try {
        out = await postForSignature(base, apiFilename, b64, ctrl);
      } catch (e: any) {
        if (e?.name === 'AbortError') throw e;
        console.warn('[rtc] sign POST fehlgeschlagen → getkill + Retry', e);
        try { await fetch(`${base}/Sign/getkill`, { method: 'GET', signal: ctrl.signal }); } catch { /* */ }
        await new Promise((r) => setTimeout(r, 1500)); // Signpad-Reinit abwarten
        out = await postForSignature(base, `r_${apiFilename}`, b64, ctrl);
      }
      // Aufräumen für das nächste Dokument (nicht blockierend; Ergebnis liegt schon in `out`).
      try { void fetch(`${base}/Sign/getkill`, { method: 'GET' }).catch(() => {}); } catch { /* */ }
      const ok = await sendSignedResultWithRetry(dc, sigId, filename, out, ctrl);
      if (!ok) throw new Error('Berater hat den Empfang nicht bestätigt (Verbindung)');
      const avpc = pcRef.current;
      if (avpc) void forceVideoKeyframe(avpc);
      try { dcRef.current?.send(JSON.stringify({ type: 'camera.keyframe' })); } catch { /* ignore */ }
    } catch (e: any) {
      const aborted = e?.name === 'AbortError';
      const evt: CamEvent = aborted
        ? { type: 'sign.aborted', sigId }
        : { type: 'sign.error', sigId, error: e?.message ?? 'sign-error', stage: 'local-api' };
      try { dc.send(JSON.stringify(evt)); } catch { /* ignore */ }
      console.warn('[rtc] sign-job fehler', e);
    } finally {
      window.clearTimeout(timeout);
      sigAbortRef.current = null;
      sigJobRef.current = null;
      window.dispatchEvent(new CustomEvent('bamoa:sign-instructions', { detail: { show: false } }));
    }
  }

  // Welle 8c-pre42: ein einzelner Signier-Post an die lokale SignController-API. Loggt den
  // HTTP-Status (für Diagnose im Stele-Log) und liefert das signierte PDF als Bytes zurück.
  async function postForSignature(
    base: string, apiFilename: string, b64: string, ctrl: AbortController,
  ): Promise<Uint8Array<ArrayBuffer>> {
    const res = await fetch(`${base}/Sign/PostFileBase64`, {
      method: 'POST',
      headers: { filename: apiFilename, 'Content-Type': 'text/plain' },
      body: b64,
      redirect: 'follow',
      signal: ctrl.signal,
    });
    console.log('[rtc] sign POST status', res.status, apiFilename);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const signedB64 = (await res.text()).trim().replace(/^data:[^,]*,/, '');
    if (!signedB64) throw new Error('leere Antwort vom Signpad');
    const raw = atob(signedB64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  }

  // Welle 8c-pre41: signiertes PDF zuverlässig an den Berater senden. Komplette Nutzlast
  // (start → Chunks → end), dann auf sign.result.ack warten; bleibt die Bestätigung aus
  // (z. B. nach kurzem Verbindungs-Hänger), wird die gesamte Nutzlast erneut gesendet
  // (bis 5 Versuche). Backpressure WARTET statt abzubrechen; bei geschlossenem Kanal wird
  // auf Wiederöffnen gewartet. So geht die Übertragung auch bei schlechter Verbindung nicht
  // verloren.
  async function sendSignedResultWithRetry(
    dc: RTCDataChannel, sigId: string, filename: string, out: Uint8Array<ArrayBuffer>, ctrl: AbortController,
  ): Promise<boolean> {
    const total = out.length;
    const totalChunks = Math.ceil(total / SCAN_CHUNK_SIZE);
    const MAX_ATTEMPTS = 5;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      if (ctrl.signal.aborted) return false;
      if (!(await waitForChannelOpen(dc, ctrl, 30000))) continue;
      // ACK-Warter registrieren, BEVOR gesendet wird
      const ackP = new Promise<boolean>((resolve) => {
        sigResultAckRef.current.set(sigId, () => resolve(true));
        const to = window.setTimeout(() => {
          if (sigResultAckRef.current.has(sigId)) { sigResultAckRef.current.delete(sigId); resolve(false); }
        }, 15000);
        ctrl.signal.addEventListener('abort', () => { window.clearTimeout(to); resolve(false); }, { once: true });
      });
      let broke = false;
      try {
        dc.send(JSON.stringify({
          type: 'sign.result.start', sigId, filename, totalBytes: total, totalChunks, chunkSize: SCAN_CHUNK_SIZE,
        } as CamEvent));
        const TARGET = 250 * 1024, THRESH = 48 * 1024, t0 = Date.now();
        let sent = 0;
        for (let i = 0; i < totalChunks; i++) {
          if (ctrl.signal.aborted) return false;
          const s = i * SCAN_CHUNK_SIZE, en = Math.min(s + SCAN_CHUNK_SIZE, total);
          let waited = 0;
          while (dc.bufferedAmount > THRESH) {
            await new Promise((r) => setTimeout(r, 5));
            if (ctrl.signal.aborted) return false;
            if (dc.readyState !== 'open') { broke = true; break; }
            if ((waited += 5) > 60000) { broke = true; break; }
          }
          if (broke || dc.readyState !== 'open') { broke = true; break; }
          dc.send(out.subarray(s, en));
          sent += en - s;
          const pct = total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 100;
          window.dispatchEvent(new CustomEvent('bamoa:sign-progress', { detail: { phase: 'returning', pct } }));
          const ahead = (sent / TARGET) * 1000 - (Date.now() - t0);
          if (ahead > 0) await new Promise((r) => setTimeout(r, Math.min(ahead, 40)));
        }
        if (!broke) dc.send(JSON.stringify({ type: 'sign.result.end', sigId } as CamEvent));
      } catch {
        broke = true;
      }
      if (broke) { sigResultAckRef.current.delete(sigId); continue; }
      const acked = await ackP;
      if (acked) return true;
      console.warn(`[rtc] sign.result.ack ausgeblieben (Versuch ${attempt}/${MAX_ATTEMPTS}) — sende erneut`);
    }
    return false;
  }

  function waitForChannelOpen(dc: RTCDataChannel, ctrl: AbortController, timeoutMs: number): Promise<boolean> {
    if (dc.readyState === 'open') return Promise.resolve(true);
    return new Promise((resolve) => {
      const t0 = Date.now();
      const iv = window.setInterval(() => {
        if (dc.readyState === 'open') { window.clearInterval(iv); resolve(true); }
        else if (ctrl.signal.aborted || Date.now() - t0 > timeoutMs) { window.clearInterval(iv); resolve(false); }
      }, 100);
    });
  }

  // Welle 8c-pre38: Berater bricht ab → laufenden Fetch abbrechen + lokalen Signpad-Reset
  // (GET /Sign/getkill) + Anleitung ausblenden + Bestätigung an den Berater.
  async function abortSigningJob(sigId: string, replyDc: RTCDataChannel | null) {
    const dc = replyDc ?? dcRef.current;
    try { sigAbortRef.current?.abort(); } catch { /* */ }
    try {
      const base = (window as any).__BAMOA_SIGN_BASE__ || 'http://127.0.0.1:8080';
      await fetch(`${base}/Sign/getkill`, { method: 'GET' });
    } catch (e) { console.warn('[rtc] sign getkill fehlgeschlagen', e); }
    sigInBufferRef.current = null;
    sigJobRef.current = null;
    window.dispatchEvent(new CustomEvent('bamoa:sign-instructions', { detail: { show: false } }));
    try { dc?.send(JSON.stringify({ type: 'sign.aborted', sigId } as CamEvent)); } catch { /* ignore */ }
  }

  return {
    status,
    error,
    localStreamMain,
    localStreamPreview,
    remoteStreamMain,
    remoteStreamPreview,
    remoteStreamAudio,
    companionScreenStream,
    remoteExpertStream,
    expertName,
    remoteMainMeta,
    remotePreviewMeta,
    hasPreview,
    connectionInfo,
    remoteRecording,
    hangup(reason = 'local-hangup') { teardown(reason); },
    toggleMic() {
      const at = localAudioTrackRef.current;
      if (!at) return false;
      at.enabled = !at.enabled;
      return at.enabled;
    },
    toggleCamera() {
      let enabled = false;
      localVideoTracksRef.current.forEach((v) => {
        const t = v.pipeline.outputTrack;
        if (t) {
          t.enabled = !t.enabled;
          enabled = t.enabled;
        }
      });
      return enabled;
    },
    requestSwap() {
      console.log('[rtc] requesting swap');
      socketRef.current?.emit('rtc:swap');
    },
    sendNfcEvent(payload: Record<string, unknown>) {
      // Welle 8b: NFC läuft P2P über die cam-ctrl DataChannels (DTLS, Ende-zu-Ende),
      // NICHT über den Server. Welle 12 (pre130): zusätzlich an den Companion-Kanal,
      // damit der Companion (auf dem Terminal-Server) das EC-Protokoll ablegen kann.
      const msg = JSON.stringify({ type: 'nfc', payload });
      let sent = false;
      const av = dcRef.current;
      if (av && av.readyState === 'open') { try { av.send(msg); sent = true; } catch (e) { console.warn('[rtc] sendNfcEvent (A/V) fehlgeschlagen', e); } }
      const comp = companionDcRef.current;
      if (comp && comp.readyState === 'open') { try { comp.send(msg); sent = true; } catch (e) { console.warn('[rtc] sendNfcEvent (Companion) fehlgeschlagen', e); } }
      if (!sent) console.warn('[rtc] sendNfcEvent: kein offener DataChannel — NFC verworfen');
      return sent;
    },
  };
}
